/*!
 * a11y-toolbar.js v3.0, "Accessibility Options" visitor panel
 * ------------------------------------------------------------------
 * A visitor-controlled display preferences panel. It changes how the page LOOKS
 * for the person who opens it. It does NOT inject ARIA, does NOT generate alt
 * text, does NOT label forms, does NOT run a screen reader mode, and does NOT
 * claim WCAG or ADA conformance. Those exclusions are deliberate and documented
 * in DECISIONS.md. Read it before adding a feature.
 *
 * No dependencies. No network calls. No tracking. Nothing leaves the device.
 * Alt+A opens and closes the panel. Alt+Shift+A resets everything, always,
 * from anywhere (D-07, D-17).
 *
 * Install (attributes on the script tag win over window.A11Y_TOOLBAR):
 *   <script src="/a11y/a11y-toolbar.js"
 *           data-position="right"            right (default) or left
 *           data-color="#0a58ca"             accent, hex, >=4.5:1 against #fff
 *           data-statement="/accessibility"  accessibility statement link
 *           data-report-email="..."          address shown under Report a problem (optional; default Ryzoro)
 *           data-fonts="/a11y/fonts/"        font folder (optional; default: fonts/ beside this file)
 *           data-nonce="..."                 CSP nonce for the two <style> elements (optional)
 *           defer></script>
 *
 * Optional config (set before the script loads):
 *   window.A11Y_TOOLBAR = {
 *     position: 'right' | 'left' (also 'top-right', 'top-left', ...),
 *     accent:   '#0a58ca',
 *     statementUrl: '/accessibility',
 *     reportEmail: '',                // same as data-report-email
 *     fontsUrl: '',                   // same as data-fonts
 *     nonce: '',                      // same as data-nonce
 *     hotkey:  true,                  // Alt+A opens the panel
 *     storage: true,                  // remember settings via localStorage
 *     filterEffects: true             // enables Color strength and Invert colors (see README caveat)
 *   };
 *
 * The dyslexia font is the only thing that ever touches the network, only
 * after the visitor turns it on, and only from this site or the server this
 * file came from. Report a problem shows an email address with a Copy
 * button; it opens nothing and sends nothing.
 */
(function () {
  'use strict';

  if (window.__a11yToolbarLoaded) return;
  window.__a11yToolbarLoaded = true;

  var ACCENT = '#0a58ca';
  var CFG = Object.assign({
    position: 'right',
    accent: ACCENT,
    statementUrl: '',
    reportEmail: '',
    fontsUrl: '',
    nonce: '',
    hotkey: true,
    storage: true,
    filterEffects: true
  }, window.A11Y_TOOLBAR || {});

  /* The report box and its data-report (reportUrl) option were removed in
     0.5.2 (D-30). A page that still sets either gets the Report a problem
     row like any other, and one console warning. */
  var OLD_REPORT = !!CFG.reportUrl;

  /* Read while this script is executing: currentScript is null afterwards. */
  var SCRIPT = document.currentScript || document.querySelector('script[src*="a11y-toolbar"]');
  if (SCRIPT) {
    if (SCRIPT.getAttribute('data-position')) CFG.position = SCRIPT.getAttribute('data-position');
    if (SCRIPT.getAttribute('data-color')) CFG.accent = SCRIPT.getAttribute('data-color');
    if (SCRIPT.getAttribute('data-statement')) CFG.statementUrl = SCRIPT.getAttribute('data-statement');
    if (SCRIPT.getAttribute('data-report-email')) CFG.reportEmail = SCRIPT.getAttribute('data-report-email');
    if (SCRIPT.hasAttribute('data-report')) OLD_REPORT = true;
    if (SCRIPT.getAttribute('data-fonts')) CFG.fontsUrl = SCRIPT.getAttribute('data-fonts');
    /* A strict Content Security Policy blocks our two <style> elements
       unless they carry its nonce. data-nonce gives it; failing that, the
       script tag's own nonce is tried, which many policies share with
       styles. The IDL property, because browsers hide the attribute. */
    if (SCRIPT.getAttribute('data-nonce')) CFG.nonce = SCRIPT.getAttribute('data-nonce');
    else if (!CFG.nonce && SCRIPT.nonce) CFG.nonce = SCRIPT.nonce;
  }
  CFG.accent = checkAccent(String(CFG.accent || ''));
  CFG.reportEmail = checkEmail(String(CFG.reportEmail || ''));
  if (OLD_REPORT) warn('data-report was removed in 0.5.2 and is ignored; Report a problem shows an email address (data-report-email).');
  /* Where this file was served from. When it is hosted on a Ryzoro server
     rather than the client's site, the fonts beside it come from there too. */
  var SCRIPT_URL = null;
  try { SCRIPT_URL = SCRIPT && SCRIPT.src ? new URL(SCRIPT.src) : null; } catch (e) { SCRIPT_URL = null; }
  /* The font folder defaults to fonts/ next to this script, as an absolute
     URL on the script's own server. data-fonts is resolved against the page,
     like any other URL attribute. */
  var FONTS = onThisSite(CFG.fontsUrl || (SCRIPT_URL ? new URL('fonts/', SCRIPT_URL).href : 'fonts/'), 'data-fonts', true);
  if (FONTS && FONTS.slice(-1) !== '/') FONTS += '/';

  /* The font folder is downloaded from, so it must be on this site: the
     widget makes no third-party calls (CLAUDE.md rule 5). It may also be on
     the server this script came from, which is the one the site already
     loads the toolbar from (D-27). Returns the absolute
     URL, or '' if it is on neither. The URL parser percent-encodes quotes,
     so the result is safe to write into CSS. */
  function onThisSite(url, what, orScriptSite) {
    if (!url) return '';
    var u = null;
    try { u = new URL(String(url), document.baseURI); } catch (e) { u = null; }
    var same = function (site) { return site && u.protocol === site.protocol && u.host === site.host; };
    if (!u || !/^(https?|file):$/.test(u.protocol) || !(same(location) || (orScriptSite && same(SCRIPT_URL)))) {
      warn(what + ' "' + url + '" is not on this site; ignored.');
      return '';
    }
    return u.href;
  }

  /* data-report-email replaces the default address. Anything that does not
     look like a plain address is refused: it is what visitors copy and
     paste into their own email. */
  function checkEmail(e) {
    if (!e) return '';
    if (!/^[^\s@<>"'?&#%,;:\/\\]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(e)) {
      warn('report email "' + e + '" is not a plain email address; using the default.');
      return '';
    }
    return e;
  }

  /* The accent fills the launcher, the switches and pressed choices, all with
     white on top, so it needs 4.5:1 against #fff. It is also written into CSS,
     so anything that is not a plain hex colour is refused. */
  function checkAccent(c) {
    if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c)) {
      warn('accent "' + c + '" is not a hex colour; using ' + ACCENT + '.');
      return ACCENT;
    }
    var ratio = contrastWithWhite(c);
    if (ratio < 4.5) {
      warn('accent ' + c + ' is ' + ratio.toFixed(2) + ':1 against white. White text on it needs 4.5:1. Pick a darker colour.');
    }
    return c;
  }
  function contrastWithWhite(hex) {
    var h = hex.slice(1);
    if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
    var lum = [0, 2, 4].map(function (i) {
      var v = parseInt(h.slice(i, i + 2), 16) / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    var L = 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
    return 1.05 / (L + 0.05);
  }
  function warn(msg) {
    if (window.console && window.console.warn) window.console.warn('a11y-toolbar: ' + msg);
  }

  var KEY = 'a11y-toolbar-prefs-v2';
  var HIDE_KEY = 'a11y-toolbar-hidden';
  var HOST_ID = 'a11y-toolbar-host';
  var PAGE_ID = 'a11y-page';
  var TIP_ID = 'a11y-tip';
  var FS_ATTR = 'data-a11y-basefs';

  var DEFAULTS = {
    profile: null,
    profileWas: null,   // what the active profile changed, and from what
    textScale: 0,       // 0-4
    spacing: 0,         // 0-2, letter spacing (storage key predates the split)
    wordSpacing: 0,     // 0-2
    lineHeight: 0,      // 0-3
    align: 0,           // 0-3
    contrast: 'none',   // none | dark | light | auto (Color theme)
    highContrast: false,
    saturation: 0,      // 0 default, 1 less, 2 gray, 3 more
    invert: false,
    textColor: '',      // '' or #rrggbb
    bgColor: '',        // '' or #rrggbb
    readableFont: false,
    dyslexiaFont: false,
    links: false,
    headings: false,
    focusRing: false,
    cursor: 'off',      // off | black | white
    stopMotion: false,
    guide: false,
    mask: false,
    mute: false,
    hideImages: false,
    tooltips: false,
    structure: false,
    linkList: false,
    readRate: 1,        // 0 slow, 1 normal, 2 fast
    oversized: false,
    dock: 'default'     // default | left | right | hidden
  };

  var SCALES = [1, 1.15, 1.3, 1.5, 1.75];
  var RATES = [0.8, 1, 1.25];
  var CONTRASTS = { none: 1, dark: 1, light: 1, auto: 1 };
  var CURSORS = { off: 1, black: 1, white: 1 };
  var DOCKS = { 'default': 1, left: 1, right: 1, hidden: 1 };
  /* Pairs that cannot both be on: turning one on turns the other off. */
  var EXCLUSIVE = { readableFont: 'dyslexiaFont', dyslexiaFont: 'readableFont' };
  var HEX6 = /^#[0-9a-f]{6}$/i;
  /* What a colour picker shows while its colour is not set. */
  var SWATCH = { textColor: '#000000', bgColor: '#ffffff' };
  /* Stepper readouts. Kept short: they sit between the minus and plus buttons. */
  var OUT = {
    textScale: ['100%', '115%', '130%', '150%', '175%'],
    spacing: ['Default', 'Wide', 'Wider'],
    wordSpacing: ['Default', 'Wide', 'Wider'],
    lineHeight: ['Default', '1.5', '1.8', '2.1']
  };
  var MAXES = { textScale: 4, spacing: 2, wordSpacing: 2, lineHeight: 3, align: 3, saturation: 3, readRate: 2 };

  /* Profiles, named for conditions by owner decision (D-15, reversing D-04).
     A profile is a shortcut to the individual rows, not a mode: turning one
     on sets those rows, which visibly change, and the visitor can undo any
     part of it. Turning it off puts back only what it set and the visitor has
     not changed since. `desc` is the 13px second line; `descFx` is added
     when filter effects are enabled, because only then can it set Color
     strength. `id` values are storage keys: never rename one. They differ
     from the July preset ids on purpose, so an old saved preset is cleared
     rather than mistaken for a new one (D-23). */
  var PROFILES = [
    { id: 'low-vision', label: 'Low vision', icon: 'visibility',
      desc: 'Bigger text, high contrast, black cursor, outlined links',
      patch: { textScale: 3, highContrast: true, cursor: 'black', links: true } },
    { id: 'dyslexia', label: 'Dyslexia friendly', icon: 'menu_book',
      desc: 'Dyslexia font, wider spacing, reading guide',
      patch: { dyslexiaFont: true, lineHeight: 2, spacing: 2, wordSpacing: 2, guide: true } },
    { id: 'adhd', label: 'Focus / ADHD', icon: 'filter_center_focus',
      desc: 'Reading mask, stop motion', descFx: ', less color',
      patch: { mask: true, stopMotion: true, saturation: 1 } },
    { id: 'seizure', label: 'Seizure safe', icon: 'flash_off',
      desc: 'Stop motion, mute sound', descFx: ', less color',
      patch: { stopMotion: true, mute: true, saturation: 1 } },
    { id: 'keyboard', label: 'Keyboard only', icon: 'keyboard',
      desc: 'Strong focus ring, outlined links and headings',
      patch: { links: true, headings: true, focusRing: true } },
    { id: 'older', label: 'Older adults', icon: 'elderly',
      desc: 'Bigger text, more line spacing, high contrast, black cursor',
      patch: { textScale: 2, lineHeight: 1, highContrast: true, cursor: 'black' } }
  ];
  /* The July 2025 preset ids. Found in storage, they are cleared and nothing
     is applied in their place (D-23). */
  var OLD_PROFILES = { lowvision: 1, reading: 1, simple: 1, motion: 1, focusaid: 1, colour: 1, targets: 1 };

  /* Whether this browser can read aloud. Checked once; never used to speak
     until the visitor presses Play. */
  var SPEECH = !!(window.speechSynthesis && typeof window.SpeechSynthesisUtterance === 'function');

  /* Panel layout (phase 1 spec, D-19): sections of labeled rows, one control
     per row. Only features that exist get a row. Profiles come first (phase
     3), then the spec's Text, Color, Focus and Navigate, then Panel (D-20).
     The Report a problem row follows them (D-30, D-31).
     type: 'switch' (role=switch), 'step' (minus / readout / plus), 'choice'
     (segmented aria-pressed buttons), 'color' (a colour input and a Reset
     button) or 'read' (Play, Pause, Stop). A switch with `value` is on when
     state[key] === value and returns state[key] to its default when turned
     off. A setting with more than two exclusive values is a choice, never a
     set of switches (phase 2 task 4). `list` names the inline list a switch
     opens below its row. `sub` is a 13px second line under the label.
     `speech` rows are disabled where the browser cannot read aloud. */
  var SECTIONS = [
    { id: 'profiles', title: 'Profiles', rows: PROFILES.map(function (p) {
      return { type: 'switch', key: 'profile', value: p.id, label: p.label, icon: p.icon,
        sub: p.desc + (CFG.filterEffects && p.descFx ? p.descFx : '') };
    }) },
    { id: 'text', title: 'Text', rows: [
      { type: 'step', key: 'textScale', label: 'Bigger text', say: 'text size', icon: 'format_size' },
      { type: 'step', key: 'lineHeight', label: 'Line spacing', say: 'line spacing', icon: 'format_line_spacing' },
      { type: 'step', key: 'spacing', label: 'Letter spacing', say: 'letter spacing', icon: 'format_letter_spacing' },
      { type: 'step', key: 'wordSpacing', label: 'Word spacing', say: 'word spacing', icon: 'space_bar' },
      { type: 'switch', key: 'readableFont', label: 'Readable font', icon: 'match_case' },
      { type: 'switch', key: 'dyslexiaFont', label: 'Dyslexia font', icon: 'font_download' },
      { type: 'choice', key: 'align', label: 'Align text', icon: 'format_align_left',
        options: [[0, 'Default'], [1, 'Left'], [2, 'Center'], [3, 'Right']] }
    ] },
    { id: 'color', title: 'Color', rows: [
      { type: 'choice', key: 'contrast', label: 'Color theme', icon: 'dark_mode',
        options: [['none', 'Default'], ['dark', 'Dark'], ['light', 'Light'], ['auto', 'Device']] },
      { type: 'switch', key: 'highContrast', label: 'High contrast', icon: 'contrast' },
      /* Display order is not storage order: 2 was Grayscale before More existed. */
      { type: 'choice', key: 'saturation', label: 'Color strength', icon: 'palette', filter: true,
        options: [[0, 'Default'], [1, 'Less'], [3, 'More'], [2, 'Gray']] },
      { type: 'switch', key: 'invert', label: 'Invert colors', icon: 'invert_colors', filter: true },
      { type: 'color', key: 'textColor', label: 'Text color', icon: 'format_color_text' },
      { type: 'color', key: 'bgColor', label: 'Background color', icon: 'format_color_fill' }
    ] },
    { id: 'focus', title: 'Focus', rows: [
      { type: 'switch', key: 'guide', label: 'Reading guide', icon: 'border_horizontal' },
      { type: 'switch', key: 'mask', label: 'Reading mask', icon: 'view_day' },
      { type: 'choice', key: 'cursor', label: 'Big cursor', icon: 'arrow_selector_tool',
        options: [['off', 'Off'], ['black', 'Black'], ['white', 'White']] },
      { type: 'switch', key: 'stopMotion', label: 'Stop motion', icon: 'motion_photos_paused' },
      { type: 'switch', key: 'mute', label: 'Mute sound', icon: 'volume_off' },
      { type: 'switch', key: 'hideImages', label: 'Hide images', icon: 'hide_image' }
    ] },
    { id: 'navigate', title: 'Navigate', rows: [
      { type: 'read', label: 'Read aloud', icon: 'record_voice_over', speech: true },
      { type: 'choice', key: 'readRate', label: 'Reading speed', icon: 'speed', speech: true,
        options: [[0, 'Slow'], [1, 'Normal'], [2, 'Fast']] },
      { type: 'switch', key: 'structure', label: 'Page outline', icon: 'toc', list: 'struct' },
      { type: 'switch', key: 'linkList', label: 'All links', icon: 'format_list_bulleted', list: 'links' },
      { type: 'switch', key: 'headings', label: 'Outline headings', icon: 'title' },
      { type: 'switch', key: 'links', label: 'Outline links', icon: 'link' },
      { type: 'switch', key: 'focusRing', label: 'Strong focus ring', icon: 'center_focus_strong' },
      { type: 'switch', key: 'tooltips', label: 'Show tooltips', icon: 'tooltip' }
    ] },
    { id: 'panel', title: 'Panel', rows: [
      { type: 'switch', key: 'oversized', label: 'Bigger panel', icon: 'zoom_in' },
      { type: 'choice', key: 'dock', label: 'Button side', icon: 'swap_horiz',
        options: [['default', 'Default'], ['left', 'Left'], ['right', 'Right'], ['hidden', 'Hide']] }
    ] }
  ];

  var state = load();
  var root = document.documentElement;
  /* classList leaves class="" behind once the last class is removed, so
     apply() takes the attribute off again if the page never had one. */
  var rootHadClass = root.hasAttribute('class');
  var panelOpen = false;
  var hiddenNow = loadHidden();
  var shadow, panel, toggleBtn, restoreBtn, wrapEl, liveRegion, guideEl, maskEl, tipEl, markEl, host, pageEl, effectsEl, addrEl, copyBtn;
  var darkQuery = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

  /* ---------- storage ---------- */
  function load() {
    var raw = null;
    if (CFG.storage) {
      try { raw = window.localStorage.getItem(KEY); } catch (e) { raw = null; }
    }
    if (raw === null) return firstVisit();
    try {
      return sanitise(Object.assign({}, DEFAULTS, JSON.parse(raw)));
    } catch (e) {
      return Object.assign({}, DEFAULTS);
    }
  }

  /* With nothing saved, start from what the visitor already told their device.
     init() saves straight away, so this happens once: a visitor who turns
     either setting off is not overruled on the next page. */
  function firstVisit() {
    var s = Object.assign({}, DEFAULTS);
    if (media('(prefers-reduced-motion: reduce)')) s.stopMotion = true;
    if (media('(prefers-contrast: more)')) s.highContrast = true;
    return s;
  }
  function media(q) {
    return !!(window.matchMedia && window.matchMedia(q).matches);
  }
  function save() {
    if (!CFG.storage) return;
    try { window.localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
  }

  /* "Hide until I close this tab" lasts for this visit only, so it lives in
     sessionStorage, apart from the saved preferences. */
  function loadHidden() {
    if (!CFG.storage) return false;
    try { return window.sessionStorage.getItem(HIDE_KEY) === '1'; } catch (e) { return false; }
  }
  function saveHidden() {
    if (!CFG.storage) return;
    try {
      if (hiddenNow) window.sessionStorage.setItem(HIDE_KEY, '1');
      else window.sessionStorage.removeItem(HIDE_KEY);
    } catch (e) { /* private mode */ }
  }

  /* Discards stored values this build does not support, so a preference saved by
     an older build (or hand-edited in devtools) falls back to a default instead
     of applying something unreachable. */
  function sanitise(s) {
    clean(s);
    /* An unknown profile id is dropped. That includes the July preset ids
       (OLD_PROFILES): the visitor's individual settings stay as saved, and
       no new profile is applied in the old one's place (D-23). */
    if (s.profile && (OLD_PROFILES[s.profile] || !profileById(s.profile))) s.profile = null;
    s.profileWas = s.profile ? cleanWas(profileById(s.profile), s.profileWas) : null;
    return s;
  }
  function clean(s) {
    if (!CONTRASTS[s.contrast]) s.contrast = 'none';
    if (!DOCKS[s.dock]) s.dock = 'default';
    /* Big cursor was a switch until phase 3; a saved true becomes Black. */
    if (s.cursor === true) s.cursor = 'black';
    if (!CURSORS[s.cursor]) s.cursor = 'off';
    Object.keys(MAXES).forEach(function (k) {
      var n = Math.round(Number(s[k]));
      s[k] = isFinite(n) ? Math.min(MAXES[k], Math.max(0, n)) : DEFAULTS[k];
    });
    Object.keys(DEFAULTS).forEach(function (k) {
      if (typeof DEFAULTS[k] === 'boolean') s[k] = s[k] === true;
    });
    /* Colours are written into CSS, so only a plain #rrggbb gets through. */
    ['textColor', 'bgColor'].forEach(function (k) {
      s[k] = typeof s[k] === 'string' && HEX6.test(s[k]) ? s[k].toLowerCase() : '';
    });
    if (!CFG.filterEffects) { s.saturation = 0; s.invert = false; }
    if (s.readableFont && s.dyslexiaFont) s.readableFont = false;
    return s;
  }
  /* The saved "what the profile changed, and from what" record. Only the
     keys this profile changes are kept, each cleaned like a setting; a
     missing one falls back to its default. */
  function cleanWas(p, was) {
    var from = was && typeof was === 'object' ? was : {};
    var tidy = clean(Object.assign({}, DEFAULTS, from));
    var out = {};
    Object.keys(profileChanges(p)).forEach(function (k) {
      out[k] = Object.prototype.hasOwnProperty.call(from, k) ? tidy[k] : DEFAULTS[k];
    });
    return out;
  }

  function profileById(id) {
    for (var i = 0; i < PROFILES.length; i++) if (PROFILES[i].id === id) return PROFILES[i];
    return null;
  }

  /* Everything a profile sets: its patch, less Color strength when filter
     effects are off, plus the other half of any exclusive pair it turns on
     (Dyslexia font turns Readable font off). */
  function profileChanges(p) {
    var out = {};
    Object.keys(p.patch).forEach(function (k) {
      if (k === 'saturation' && !CFG.filterEffects) return;
      out[k] = p.patch[k];
      var other = EXCLUSIVE[k];
      if (p.patch[k] === true && other && !(other in p.patch)) out[other] = false;
    });
    return out;
  }
  /* Remembers what each setting was, so turning the profile off can put it
     back. */
  function profileOn(p) {
    var ch = profileChanges(p), was = {};
    Object.keys(ch).forEach(function (k) { was[k] = state[k]; state[k] = ch[k]; });
    state.profile = p.id;
    state.profileWas = was;
  }
  /* Puts back only what the profile set and the visitor has not changed by
     hand since: a setting that no longer holds the profile's value is the
     visitor's now, and is left alone. */
  function profileOff() {
    var p = profileById(state.profile);
    if (p) {
      var ch = profileChanges(p), was = state.profileWas || {};
      Object.keys(ch).forEach(function (k) {
        if (state[k] === ch[k]) state[k] = k in was ? was[k] : DEFAULTS[k];
      });
      if (state.readableFont && state.dyslexiaFont) state.readableFont = false;
    }
    state.profile = null;
    state.profileWas = null;
  }

  /* ---------- effects stylesheet (light DOM) ---------- */
  var GUIDE_ID = 'a11y-reading-guide';
  var MASK_ID = 'a11y-reading-mask';
  var MARK_ID = 'a11y-read-mark';
  var NOT_MINE = ':not(#' + HOST_ID + '):not(#' + HOST_ID + ' *):not(#' + TIP_ID + ')' +
    ':not(#' + GUIDE_ID + '):not(#' + MASK_ID + '):not(#' + MARK_ID + ')';

  /* Page filters. Saturation and Invert genuinely need a CSS `filter`, which
     per CSS Filter Effects 1 creates a containing block for fixed-position
     descendants. So they go on a page wrapper that the toolbar sits OUTSIDE
     of, never on <html> or <body> (D-06). One rule per combination, because
     two rules setting `filter` on the same element do not add up. Invert
     turns photos and video back the right way round, so they still look like
     what they show. */
  var SAT = { 1: 'saturate(.45)', 2: 'grayscale(1)', 3: 'saturate(1.8)' };
  var INV = 'invert(1) hue-rotate(180deg)';
  function filterRules() {
    var out = [];
    [false, true].forEach(function (inv) {
      [0, 1, 2, 3].forEach(function (s) {
        if (!inv && !s) return;
        var sel = '#' + PAGE_ID + (inv ? '.a11yfx-inv' : '') + (s ? '.a11yfx-sat' + s : '');
        out.push(sel + '{filter:' + (inv ? INV + (s ? ' ' : '') : '') + (SAT[s] || '') + '}');
      });
    });
    var p = '#' + PAGE_ID + '.a11yfx-inv ';
    out.push(p + 'img,' + p + 'video,' + p + 'iframe,' + p + 'canvas{filter:' + INV + '}');
    return out;
  }

  /* Two-tone ring, white inside and black outside, so one of the pair meets
     3:1 against any page background. It is a box-shadow, not an outline, so
     the site's own focus outline still shows on a focused link. */
  var RING = '{text-decoration:underline!important;text-decoration-thickness:2px!important;text-underline-offset:3px!important;box-shadow:0 0 0 2px #fff,0 0 0 4px #000!important}';

  /* Big cursor: our own artwork, an arrow for the page and a pointing hand
     for things that can be clicked, drawn 52px from a 32-unit grid. Each is
     drawn twice: once as a thick outline in the edge colour, then filled on
     top, so the edge is one clean 1px line round the whole shape. */
  var ARROW = '<path d="M6 2l20 15-9 1 5 10-4 2-5-10-7 6z"/>';
  /* index finger, three curled fingers, palm, thumb */
  var HAND = '<rect x="11" y="1.5" width="5" height="17" rx="2.5"/><rect x="16" y="9.5" width="4" height="10" rx="2"/>' +
    '<rect x="20" y="11" width="4" height="9" rx="2"/><rect x="24" y="13" width="3.6" height="8" rx="1.8"/>' +
    '<rect x="8" y="15" width="19.6" height="14.5" rx="4.5"/><rect x="4" y="15.5" width="7" height="4.6" rx="2.3"/>';
  function cursorURL(shape, fill, edge) {
    /* 1.23 units of stroke, half of it outside the fill, is 1px at 52px */
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 32 32">' +
      '<g fill="' + edge + '" stroke="' + edge + '" stroke-width="1.23" stroke-linejoin="round">' + shape + '</g>' +
      '<g fill="' + fill + '">' + shape + '</g></svg>';
    return 'url("data:image/svg+xml,' + encodeURIComponent(svg) + '")';
  }
  /* Hotspots: the arrow's tip and the fingertip, in 52px image pixels. */
  function cursorRules(name, fill, edge) {
    var h = 'html.a11yfx-cur-' + name;
    return [
      h + ',' + h + ' *{cursor:' + cursorURL(ARROW, fill, edge) + ' 10 3,auto!important}',
      [' a', ' a *', ' button', ' button *', ' [role="button"]', ' label', ' summary', ' select'].map(function (s) { return h + s; }).join(',') +
        '{cursor:' + cursorURL(HAND, fill, edge) + ' 22 2,pointer!important}'
    ];
  }

  var EFFECTS = [
    /* Color theme and High contrast are class-based recolouring, not filters.
       A theme sets the scheme; High contrast pushes that scheme to pure black
       and white with solid borders. High contrast on the Default theme uses
       the light scheme (D-21). */

    /* dark theme */
    'html.a11yfx-dark,html.a11yfx-dark body{background:#121212!important;color:#e8e8e8!important}',
    'html.a11yfx-dark *' + NOT_MINE + '{background-color:transparent!important;color:#e8e8e8!important;border-color:#6b6b6b!important;text-shadow:none!important;box-shadow:none!important}',
    'html.a11yfx-dark a' + NOT_MINE + '{color:#8ab4f8!important;text-decoration:underline!important}',
    'html.a11yfx-dark button' + NOT_MINE + ',html.a11yfx-dark input,html.a11yfx-dark select,html.a11yfx-dark textarea{background:#1e1e1e!important;color:#e8e8e8!important;border:1px solid #8a8a8a!important}',
    'html.a11yfx-dark svg' + NOT_MINE + '{fill:currentColor!important}',

    /* light theme */
    'html.a11yfx-light,html.a11yfx-light body{background:#fff!important;color:#1a1a1a!important}',
    'html.a11yfx-light *' + NOT_MINE + '{background-color:transparent!important;color:#1a1a1a!important;border-color:#767676!important;text-shadow:none!important}',
    'html.a11yfx-light a' + NOT_MINE + '{color:#0b57d0!important;text-decoration:underline!important}',
    'html.a11yfx-light button' + NOT_MINE + ',html.a11yfx-light input,html.a11yfx-light select,html.a11yfx-light textarea{background:#fff!important;color:#1a1a1a!important;border:1px solid #767676!important}',

    /* high contrast, dark scheme */
    'html.a11yfx-hc.a11yfx-dark,html.a11yfx-hc.a11yfx-dark body{background:#000!important;color:#fff!important}',
    'html.a11yfx-hc.a11yfx-dark *' + NOT_MINE + '{background-color:transparent!important;color:#fff!important;border-color:#fff!important;text-shadow:none!important;box-shadow:none!important}',
    'html.a11yfx-hc.a11yfx-dark a' + NOT_MINE + '{color:#ffe74c!important;text-decoration:underline!important}',
    'html.a11yfx-hc.a11yfx-dark button' + NOT_MINE + ',html.a11yfx-hc.a11yfx-dark input,html.a11yfx-hc.a11yfx-dark select,html.a11yfx-hc.a11yfx-dark textarea{background:#000!important;color:#fff!important;border:2px solid #fff!important}',

    /* high contrast, light scheme */
    'html.a11yfx-hc.a11yfx-light,html.a11yfx-hc.a11yfx-light body{background:#fff!important;color:#000!important}',
    'html.a11yfx-hc.a11yfx-light *' + NOT_MINE + '{background-color:transparent!important;color:#000!important;border-color:#000!important;text-shadow:none!important;box-shadow:none!important}',
    'html.a11yfx-hc.a11yfx-light a' + NOT_MINE + '{color:#0b3d91!important;text-decoration:underline!important}',
    'html.a11yfx-hc.a11yfx-light button' + NOT_MINE + ',html.a11yfx-hc.a11yfx-light input,html.a11yfx-hc.a11yfx-light select,html.a11yfx-hc.a11yfx-light textarea{background:#fff!important;color:#000!important;border:2px solid #000!important}'
  ].concat(filterRules(), [
    /* readable font */
    'html.a11yfx-font *' + NOT_MINE + ':not(i):not([class*="icon"]):not([class*="fa-"]){font-family:Verdana,"DejaVu Sans",Tahoma,sans-serif!important}',

    /* dyslexia font. Its @font-face is only added once the switch is on
       (fontCSS), so nothing is downloaded until then. */
    'html.a11yfx-dys *' + NOT_MINE + ':not(i):not([class*="icon"]):not([class*="fa-"]){font-family:OpenDyslexic,Verdana,sans-serif!important}',

    /* letter spacing: the WCAG 2.2 SC 1.4.12 value at step 2, with its
       paragraph spacing */
    'html.a11yfx-space1 p' + NOT_MINE + ',html.a11yfx-space1 li,html.a11yfx-space1 td,html.a11yfx-space1 dd,html.a11yfx-space1 blockquote{letter-spacing:.03em!important}',
    'html.a11yfx-space2 p' + NOT_MINE + ',html.a11yfx-space2 li,html.a11yfx-space2 td,html.a11yfx-space2 dd,html.a11yfx-space2 blockquote{letter-spacing:.12em!important}',
    'html.a11yfx-space2 p' + NOT_MINE + '{margin-bottom:2em!important}',

    /* word spacing: the SC 1.4.12 value at step 1, double it at step 2 */
    'html.a11yfx-word1 p' + NOT_MINE + ',html.a11yfx-word1 li,html.a11yfx-word1 td,html.a11yfx-word1 dd,html.a11yfx-word1 blockquote{word-spacing:.16em!important}',
    'html.a11yfx-word2 p' + NOT_MINE + ',html.a11yfx-word2 li,html.a11yfx-word2 td,html.a11yfx-word2 dd,html.a11yfx-word2 blockquote{word-spacing:.32em!important}',

    /* line height: unitless so it inherits predictably */
    'html.a11yfx-lh1 p' + NOT_MINE + ',html.a11yfx-lh1 li,html.a11yfx-lh1 td,html.a11yfx-lh1 dd,html.a11yfx-lh1 blockquote{line-height:1.5!important}',
    'html.a11yfx-lh2 p' + NOT_MINE + ',html.a11yfx-lh2 li,html.a11yfx-lh2 td,html.a11yfx-lh2 dd,html.a11yfx-lh2 blockquote{line-height:1.8!important}',
    'html.a11yfx-lh3 p' + NOT_MINE + ',html.a11yfx-lh3 li,html.a11yfx-lh3 td,html.a11yfx-lh3 dd,html.a11yfx-lh3 blockquote{line-height:2.1!important}',

    /* text align. No justify option: justified text on the web produces uneven
       word spacing ("rivers") and SC 1.4.8 asks that it be avoidable. */
    'html.a11yfx-al1 p' + NOT_MINE + ',html.a11yfx-al1 li,html.a11yfx-al1 dd,html.a11yfx-al1 blockquote{text-align:left!important}',
    'html.a11yfx-al2 p' + NOT_MINE + ',html.a11yfx-al2 li,html.a11yfx-al2 dd,html.a11yfx-al2 blockquote{text-align:center!important}',
    'html.a11yfx-al3 p' + NOT_MINE + ',html.a11yfx-al3 li,html.a11yfx-al3 dd,html.a11yfx-al3 blockquote{text-align:right!important}',

    /* Outline links and Outline headings. :root rather than html, so the ring
       outranks High contrast's box-shadow:none. */
    ':root.a11yfx-links a' + NOT_MINE + RING,
    ':root.a11yfx-heads h1' + NOT_MINE + ',:root.a11yfx-heads h2,:root.a11yfx-heads h3,:root.a11yfx-heads h4,:root.a11yfx-heads h5,:root.a11yfx-heads h6,:root.a11yfx-heads [role="heading"]' + RING,

    /* Strong focus ring: white inside, black outside, on whatever has
       keyboard focus. An outline survives Windows high contrast, which drops
       box-shadows. Outranks the Outline links ring and High contrast. */
    ':root.a11yfx-focus *' + NOT_MINE + ':focus-visible{outline:3px solid #000!important;outline-offset:3px!important;box-shadow:0 0 0 3px #fff!important}'
  ], cursorRules('black', '#000', '#fff'), cursorRules('white', '#fff', '#000'), [

    /* stop motion */
    'html.a11yfx-motion *' + NOT_MINE + '{animation-play-state:paused!important;animation-iteration-count:1!important;transition-duration:.001ms!important;scroll-behavior:auto!important}',

    /* hide images. Scoped out of controls: blanking an icon inside a button
       leaves an unlabelled empty control. */
    'html.a11yfx-noimg img' + NOT_MINE + ',html.a11yfx-noimg picture,html.a11yfx-noimg video{visibility:hidden!important}',
    'html.a11yfx-noimg svg' + NOT_MINE + ':not(button svg):not(a svg):not([role="button"] svg):not(label svg){visibility:hidden!important}',
    'html.a11yfx-noimg *' + NOT_MINE + ':not(button):not(a){background-image:none!important}',

    /* reading guide */
    '#' + GUIDE_ID + '{position:fixed;left:0;right:0;height:2.6rem;pointer-events:none;z-index:2147483646;border-top:3px solid #b30000;border-bottom:3px solid #b30000;background:rgba(0,0,0,.06);display:none}',
    'html.a11yfx-guide #' + GUIDE_ID + '{display:block}',

    /* reading mask: a clear band whose huge shadow dims the rest of the page.
       It sits under the launcher and the panel, so neither is dimmed. */
    '#' + MASK_ID + '{position:fixed;left:0;right:0;top:50%;height:7rem;transform:translateY(-50%);pointer-events:none;z-index:2147483645;box-shadow:0 0 0 200vmax rgba(0,0,0,.6);display:none}',
    'html.a11yfx-mask #' + MASK_ID + '{display:block}',
    '@media (forced-colors:active){#' + MASK_ID + '{forced-color-adjust:none}}',

    /* Read aloud: a ring round the block being read, drawn over it rather
       than added to it, so the page's own markup is never touched. Same
       two-tone ring as Outline headings; the black half is an outline so it
       survives Windows high contrast. Sits over the mask, under the panel. */
    '#' + MARK_ID + '{position:fixed;pointer-events:none;z-index:2147483645;border-radius:2px;box-shadow:0 0 0 2px #fff;outline:2px solid #000;outline-offset:2px;display:none}',
    '#' + MARK_ID + '.on{display:block}',

    /* tooltip. aria-hidden in the DOM, so AT reads the real accessible name
       rather than this duplicate. */
    '#' + TIP_ID + '{position:fixed;z-index:2147483646;max-width:22rem;padding:.4rem .6rem;background:#101317;color:#fff;font:600 .85rem/1.35 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;border-radius:.3rem;box-shadow:0 3px 12px rgba(0,0,0,.4);pointer-events:none;display:none}',
    '#' + TIP_ID + '.on{display:block}'
  ]).join('\n');

  /* Custom text and background colours. The values come from the visitor, so
     these rules are built on demand and only from a sanitised #rrggbb. Text
     colour reaches every element's text, and links keep an underline so they
     still stand out once they share the text colour. Background colour goes on
     the page itself and everything above it is made transparent, so images and
     background images are never recoloured and no overlay turns opaque.
     :root, so a visitor's own colour outranks a theme. */
  function colorCSS() {
    var out = [];
    if (state.textColor) {
      out.push(':root.a11yfx-fg body,:root.a11yfx-fg body *' + NOT_MINE + '{color:' + state.textColor + '!important}');
      out.push(':root.a11yfx-fg a' + NOT_MINE + '{text-decoration:underline!important}');
    }
    if (state.bgColor) {
      out.push(':root.a11yfx-bg,:root.a11yfx-bg body{background-color:' + state.bgColor + '!important}');
      out.push(':root.a11yfx-bg body *' + NOT_MINE + '{background-color:transparent!important}');
    }
    return out.join('\n');
  }

  /* OpenDyslexic (SIL OFL 1.1, LICENSES.md), self-hosted in the font folder.
     The @font-face is only written while Dyslexia font is on, so a visitor
     who never turns it on never downloads it. FONTS has been through the URL
     parser, so it carries no quote that could end the url(). */
  function fontCSS() {
    if (!state.dyslexiaFont || !FONTS) return '';
    return [['Regular', 400], ['Bold', 700]].map(function (f) {
      return '@font-face{font-family:OpenDyslexic;src:url("' + FONTS + 'OpenDyslexic-' + f[0] + '.woff2") format("woff2");' +
        'font-weight:' + f[1] + ';font-style:normal;font-display:swap}';
    }).join('\n');
  }

  /* ---------- panel styles (shadow DOM) ----------
     Sizes are in em against the .wrap font-size, which apply() sets from the
     visitor's own text size, so the panel grows with the page (16px base).
     Colour pairs on #fff: text #1a1a1a 17.4:1, secondary #555 7.5:1, icons
     #333 12.6:1, control boundaries and the off switch #767676 4.54:1 (SC 1.4.11
     wants 3:1). Smallest text is .8125em = 13px. */
  function panelCSS() {
    return [
      ':host{all:initial}',
      '*{box-sizing:border-box;margin:0;padding:0}',
      '.wrap{--acc:' + CFG.accent + ';font-family:system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;font-size:16px;line-height:1.4;color:#1a1a1a}',
      'svg{display:block;flex:none;fill:currentColor}',
      'button{font:inherit;color:inherit;background:none;border:0;cursor:pointer;text-align:inherit}',
      'a{color:inherit}',
      'button:focus-visible,a:focus-visible,input:focus-visible,[tabindex]:focus-visible{outline:3px solid #1a1a1a;outline-offset:2px}',

      /* launcher. The 2px white ring gives the accent fill a 3:1 boundary on a
         dark page; on a light page the fill itself provides it. The focus ring
         is dark over white so it shows on any background. */
      '.launch{position:fixed;z-index:2147483646;right:24px;bottom:24px;width:56px;height:56px;border-radius:50%;background:var(--acc);color:#fff;display:grid;place-items:center;box-shadow:0 0 0 2px #fff,0 2px 8px rgba(0,0,0,.35)}',
      '.launch svg{width:32px;height:32px}',
      '.launch:focus-visible{outline:3px solid #1a1a1a;outline-offset:2px;box-shadow:0 0 0 7px #fff,0 2px 8px rgba(0,0,0,.35)}',
      '.side-left .launch{right:auto;left:24px}',
      '.v-top .launch{bottom:auto;top:24px}',
      '.big .launch{width:72px;height:72px}',
      '.big .launch svg{width:40px;height:40px}',
      '.docked-hidden .launch,.hidden-now .launch{display:none}',

      /* restore tab: whenever the launcher is hidden, a 12x56 strip on the
         same screen edge, centred where the launcher was, in the accent with
         the launcher's white ring and the icon cut off inside it (D-31). The
         button around it is 44px wide, so the target is bigger than what
         shows. */
      '.restore{display:none;position:fixed;z-index:2147483646;right:0;bottom:24px;width:44px;height:56px;justify-content:flex-end}',
      '.docked-hidden .restore,.hidden-now .restore{display:flex}',
      '.side-left .restore{right:auto;left:0;justify-content:flex-start}',
      '.v-top .restore{bottom:auto;top:24px}',
      '.big .restore{bottom:32px}',
      '.big.v-top .restore{bottom:auto;top:32px}',
      '.tab{position:relative;width:12px;overflow:hidden;border-radius:8px 0 0 8px;background:var(--acc);color:#fff;box-shadow:0 0 0 2px #fff,0 2px 8px rgba(0,0,0,.35)}',
      '.side-left .tab{border-radius:0 8px 8px 0}',
      '.tab svg{position:absolute;top:50%;left:50%;width:24px;height:24px;margin:-12px 0 0 -12px}',
      '.restore:focus-visible{outline:0}',
      '.restore:focus-visible .tab{outline:3px solid #1a1a1a;outline-offset:2px;box-shadow:0 0 0 7px #fff,0 2px 8px rgba(0,0,0,.35)}',

      /* panel: no backdrop, page stays usable. Under 480px it is a sheet
         over the bottom half of the screen (D-32, in the phone block
         below). From 480px it is a card in the launcher's corner, no taller
         than it needs and at most min(80vh, 760px); the body scrolls inside
         it between the header and footer (D-31). */
      '.panel{position:fixed;z-index:2147483647;top:0;right:0;height:100vh;height:100dvh;width:23.75em;max-width:100%;display:flex;flex-direction:column;background:#fff;color:#1a1a1a;border-left:1px solid #d0d0d0;box-shadow:-4px 0 16px rgba(0,0,0,.12)}',
      '.side-left .panel{right:auto;left:0;border-left:0;border-right:1px solid #d0d0d0;box-shadow:4px 0 16px rgba(0,0,0,.12)}',
      '@media (min-width:480px){' +
        '.panel{top:auto;bottom:24px;right:24px;height:auto;max-height:min(80vh,760px);max-width:calc(100% - 48px);border:1px solid #d0d0d0;border-radius:12px;overflow:hidden}' +
        '.side-left .panel{right:auto;left:24px;border:1px solid #d0d0d0}' +
        '.v-top .panel{bottom:auto;top:24px}}',
      '.panel[hidden]{display:none}',
      '.panel:not([hidden]){animation:a11y-in-r .15s ease-out}',
      '.side-left .panel:not([hidden]){animation-name:a11y-in-l}',
      '@keyframes a11y-in-r{from{transform:translateX(100%)}to{transform:none}}',
      '@keyframes a11y-in-l{from{transform:translateX(-100%)}to{transform:none}}',
      '@keyframes a11y-in-b{from{transform:translateY(100%)}to{transform:none}}',

      /* header */
      '.hd{flex:none;padding:.75em 1em;border-bottom:1px solid #e6e6e6}',
      '.hd-top{display:flex;align-items:center;justify-content:space-between;gap:.5em}',
      '.hd h2{font-size:1.25em;font-weight:600;line-height:1.3}',
      '.x{flex:none;width:2.5em;height:2.5em;display:grid;place-items:center;border-radius:.375em}',
      '.x:hover{background:#f0f0f0}',
      '.x svg{width:1.5em;height:1.5em}',
      '.hint{font-size:.8125em;color:#555}',
      '.skip{display:block;width:100%;min-height:2.75em;margin-top:.5em;padding:.25em .5em;border:1px solid #767676;border-radius:.375em;font-size:.9375em;font-weight:500;text-align:center;background:#fff}',
      '.skip:hover{background:#f0f0f0}',
      /* Under 480px Skip moves into this row at the top of the body, so the
         header is one line (D-32). Empty, it takes no space. */
      '.skip-row{padding:.5em 1em;border-bottom:1px solid #e6e6e6}',
      '.skip-row:empty{display:none}',
      '.skip-row .skip{margin-top:0}',

      /* body and sections */
      '.body{flex:1 1 auto;min-height:0;overflow-y:auto;overscroll-behavior:contain}',
      '.sec{border-bottom:1px solid #e6e6e6}',
      '.sec h3{font-size:1em;font-weight:inherit}',
      '.sec-btn{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:3em;padding:0 1.0667em;font-size:.9375em;font-weight:600;background:#f7f7f7}',
      '.sec-btn:hover{background:#efefef}',
      '.sec-btn:focus-visible{outline-offset:-3px}',
      '.sec-btn svg{width:1.5em;height:1.5em;transition:transform .15s}',
      '.sec-btn[aria-expanded="false"] svg{transform:rotate(-90deg)}',

      /* rows: icon, label, control. Wraps the control under the label when the
         text size makes the row too narrow. */
      '.row{display:flex;flex-wrap:wrap;align-items:center;gap:.5em .75em;min-height:3.5em;padding:.375em 1em;border-top:1px solid #e6e6e6}',
      '.sec-body>.row:first-child{border-top:0}',
      '.ic{width:1.25em;height:1.25em;color:#333}',
      '.lbl{flex:1 1 auto;min-width:0;font-size:1em;font-weight:500}',
      'label.lbl{cursor:pointer}',
      /* A label with a 13px second line under it (profiles, unavailable
         rows). Zero basis, so the second line wraps beside the control
         instead of pushing the control, or the icon, onto a line of its own. */
      '.txt{flex:1 1 0;min-width:0;display:flex;flex-direction:column}',
      '.sub{font-size:.8125em;line-height:1.35;color:#555}',
      '.ctl{flex:none;margin-left:auto;display:flex;align-items:center}',

      /* switch: 44x24, knob position carries the state as well as the fill */
      '.sw{position:relative;width:2.75em;height:1.5em;border-radius:.75em;background:#767676}',
      '.sw::after{content:"";position:absolute;top:.1875em;left:.1875em;width:1.125em;height:1.125em;border-radius:50%;background:#fff;transition:transform .15s}',
      '.sw[aria-checked="true"]{background:var(--acc)}',
      '.sw[aria-checked="true"]::after{transform:translateX(1.25em)}',

      /* stepper: 36x36 buttons around a readout */
      '.step{display:flex;align-items:center;gap:.25em}',
      '.step button{width:2.25em;height:2.25em;display:grid;place-items:center;border:1px solid #767676;border-radius:.375em;background:#fff}',
      '.step button:hover{background:#f0f0f0}',
      '.step button svg{width:1.25em;height:1.25em}',
      '.step button[aria-disabled="true"]{color:#767676;border-color:#c8c8c8;background:#fff;cursor:default}',
      '.val{min-width:4.5em;text-align:center;font-size:.875em;font-weight:600;border-radius:.25em}',

      /* segmented choice */
      '.seg{display:flex}',
      '.seg button{position:relative;min-height:2.5em;padding:0 .7143em;font-size:.875em;font-weight:500;border:1px solid #767676;margin-left:-1px;background:#fff}',
      '.seg button:first-child{margin-left:0;border-radius:.4286em 0 0 .4286em}',
      '.seg button:last-child{border-radius:0 .4286em .4286em 0}',
      '.seg button:hover{background:#f0f0f0}',
      '.seg button[aria-pressed="true"]{background:var(--acc);border-color:var(--acc);color:#fff;z-index:1}',
      '.seg button:focus-visible{z-index:2}',
      '.seg button:disabled{color:#767676;cursor:default}',
      '.seg button:disabled:hover{background:#fff}',

      /* read aloud: Play, Pause, Stop, each an icon and a word */
      '.rd{display:flex;gap:.25em}',
      '.rd button{display:flex;align-items:center;gap:.2857em;min-height:2.5em;padding:0 .5714em;font-size:.875em;font-weight:500;border:1px solid #767676;border-radius:.4286em;background:#fff}',
      '.rd button svg{width:1.2857em;height:1.2857em}',
      '.rd button:hover{background:#f0f0f0}',
      '.rd button[aria-disabled="true"],.rd button:disabled{color:#767676;border-color:#c8c8c8;background:#fff;cursor:default}',

      /* colour picker and its Reset */
      '.clr{display:flex;align-items:center;gap:.5em}',
      '.clr input{width:2.75em;height:2.25em;padding:.125em;border:1px solid #767676;border-radius:.375em;background:#fff;cursor:pointer}',
      '.rst{min-height:2.5em;padding:0 .7143em;font-size:.875em;font-weight:500;border:1px solid #767676;border-radius:.4286em;background:#fff}',
      '.rst:hover{background:#f0f0f0}',
      '.rst[aria-disabled="true"]{color:#767676;border-color:#c8c8c8;cursor:default}',

      /* inline lists (page outline, all links) */
      '.list{padding:0 0 .5em 2.75em}',
      '.list ul{list-style:none}',
      '.list button{display:flex;align-items:center;gap:.5em;width:100%;min-height:2.75em;padding:.25em 1em .25em .5em;border-top:1px solid #e6e6e6}',
      '.list button:hover{background:#f0f0f0}',
      '.list button:focus-visible{outline-offset:-3px}',
      '.list .lvl{flex:none;min-width:4.6em;font-size:.8125em;font-weight:700;color:#555}',
      '.list .empty{padding:.75em 1em .5em .5em;font-size:.875em;color:#555}',

      /* report a problem: the address, and a Copy button 44px high. One
         click selects the whole address, for copying by hand. */
      '.mail{padding:.75em 1em 1em}',
      '.mail h3{font-size:1em;font-weight:700}',
      '.mail .note{margin:.125em 0 .5em}',
      '.addr-row{display:flex;align-items:center;gap:.5em}',
      '.addr{flex:1 1 auto;min-width:0;min-height:2.9333em;display:flex;align-items:center;padding:.25em .5em;border:1px solid #767676;border-radius:.375em;background:#f7f7f7;' +
        'font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:.9375em;overflow-wrap:anywhere;-webkit-user-select:all;user-select:all}',
      '.copy{flex:none;display:flex;align-items:center;justify-content:center;gap:.3333em;min-width:6.875em;min-height:2.9333em;padding:0 .75em;border:1px solid #767676;border-radius:.4em;font-size:.9375em;font-weight:500;background:#fff}',
      '.copy:hover{background:#f0f0f0}',
      '.copy svg{width:1.3333em;height:1.3333em;color:#333}',

      /* footer */
      '.ft{flex:none;padding:.5em 1em .75em;border-top:1px solid #d0d0d0}',
      '.note{font-size:.8125em;color:#555;margin-bottom:.5em}',
      '.ft-row{display:flex;gap:.5em}',
      '.ft-row>*{flex:1 1 0;min-height:2.75em;display:grid;place-items:center;padding:.25em .5em;border:1px solid #767676;border-radius:.375em;font-size:.9375em;font-weight:500;text-align:center;text-decoration:none;background:#fff}',
      '.ft-row>*:hover{background:#f0f0f0}',
      '.ft-row>[data-act="hide"]{flex-grow:1.75}',
      '.ft .ft-tab{margin:.5em 0 0}',
      '.sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}',

      /* Under 480px the panel is a sheet over the bottom half of the screen,
         so the visitor can see what a setting does to the page above it
         (D-32): 50dvh, or 50vh where dvh is unknown, and the footer clears
         the browser's own bottom bar. The footer buttons are at least 44px
         high; the short Hide label no longer wraps to make them so. While
         the sheet is open the launcher and the restore tab are hidden. The
         restore tab stays centred on the smaller launchers: 48px and 64px. */
      '@media (max-width:479px){' +
        '.panel,.side-left .panel{top:auto;right:0;bottom:0;left:0;width:100%;height:50vh;height:50dvh;border:0;border-top:1px solid #d0d0d0;' +
          'border-radius:12px 12px 0 0;box-shadow:0 -4px 16px rgba(0,0,0,.12);overflow:hidden}' +
        '.panel:not([hidden]),.side-left .panel:not([hidden]){animation-name:a11y-in-b}' +
        '.ft{padding-bottom:calc(.75em + env(safe-area-inset-bottom))}' +
        '.ft-row>*{min-height:2.9375em}.ft-row>[data-act="hide"]{flex-grow:1}' +
        '.open .launch,.open .restore{display:none}' +
        '.launch{width:48px;height:48px}.launch svg{width:28px;height:28px}.big .launch{width:64px;height:64px}' +
        '.restore{bottom:20px}.v-top .restore{bottom:auto;top:20px}.big .restore{bottom:28px}.big.v-top .restore{bottom:auto;top:28px}}',
      /* The keyboard lines say nothing to a touch-only device. The shortcut
         line in the footer (D-07) goes only on a touch-only phone, so a
         computer zoomed below 480px keeps it (D-32). */
      '@media (hover:none) and (pointer:coarse){.hint{display:none}}',
      '@media (max-width:479px) and (hover:none) and (pointer:coarse){.ft-save{display:none}}',
      /* Windows high contrast drops backgrounds, which would erase the switch. */
      '@media (forced-colors:active){.sw{border:2px solid ButtonText}.sw::after{background:ButtonText}.sw[aria-checked="true"]{background:Highlight}.seg button[aria-pressed="true"]{background:Highlight;color:HighlightText}}',
      /* the panel honours its own Stop motion switch as well as the OS setting */
      '.still *,.still *::after{animation:none!important;transition:none!important}',
      '@media (prefers-reduced-motion:reduce){*,*::after{animation:none!important;transition:none!important}}'
    ].join('\n');
  }

  /* ---------- icons ----------
     Material Symbols Outlined, weight 400 (Google, Apache License 2.0; see
     LICENSES.md). Path data only, inlined so nothing is downloaded at runtime. */
  var ICONS = {
    accessibility_new: 'M423.5-743.5Q400-767 400-800t23.5-56.5Q447-880 480-880t56.5 23.5Q560-833 560-800t-23.5 56.5Q513-720 480-720t-56.5-23.5ZM360-80v-520q-60-5-122-15t-118-25l20-80q78 21 166 30.5t174 9.5q86 0 174-9.5T820-720l20 80q-56 15-118 25t-122 15v520h-80v-240h-80v240h-80Z',
    add: 'M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z',
    arrow_selector_tool: 'm320-410 79-110h170L320-716v306ZM551-80 406-392 240-160v-720l560 440H516l144 309-109 51ZM399-520Z',
    border_horizontal: 'M120-120v-80h80v80h-80Zm0-160v-80h80v80h-80Zm0-160v-80h720v80H120Zm0-160v-80h80v80h-80Zm0-160v-80h80v80h-80Zm160 640v-80h80v80h-80Zm0-640v-80h80v80h-80Zm160 640v-80h80v80h-80Zm0-160v-80h80v80h-80Zm0-320v-80h80v80h-80Zm0-160v-80h80v80h-80Zm160 640v-80h80v80h-80Zm0-640v-80h80v80h-80Zm160 640v-80h80v80h-80Zm0-160v-80h80v80h-80Zm0-320v-80h80v80h-80Zm0-160v-80h80v80h-80Z',
    center_focus_strong: 'M200-120q-33 0-56.5-23.5T120-200v-160h80v160h160v80H200Zm400 0v-80h160v-160h80v160q0 33-23.5 56.5T760-120H600ZM120-600v-160q0-33 23.5-56.5T200-840h160v80H200v160h-80Zm640 0v-160H600v-80h160q33 0 56.5 23.5T840-760v160h-80ZM480-280q-83 0-141.5-58.5T280-480q0-83 58.5-141.5T480-680q83 0 141.5 58.5T680-480q0 83-58.5 141.5T480-280Zm0-80q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35Zm0-120Z',
    close: 'm256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z',
    content_copy: 'M360-240q-33 0-56.5-23.5T280-320v-480q0-33 23.5-56.5T360-880h360q33 0 56.5 23.5T800-800v480q0 33-23.5 56.5T720-240H360Zm0-80h360v-480H360v480ZM200-80q-33 0-56.5-23.5T120-160v-560h80v560h440v80H200Zm160-240v-480 480Z',
    contrast: 'M324-111.5Q251-143 197-197t-85.5-127Q80-397 80-480t31.5-156Q143-709 197-763t127-85.5Q397-880 480-880t156 31.5Q709-817 763-763t85.5 127Q880-563 880-480t-31.5 156Q817-251 763-197t-127 85.5Q563-80 480-80t-156-31.5ZM520-163q119-15 199.5-104.5T800-480q0-123-80.5-212.5T520-797v634Z',
    dark_mode: 'M480-120q-150 0-255-105T120-480q0-150 105-255t255-105q14 0 27.5 1t26.5 3q-41 29-65.5 75.5T444-660q0 90 63 153t153 63q55 0 101-24.5t75-65.5q2 13 3 26.5t1 27.5q0 150-105 255T480-120Zm0-80q88 0 158-48.5T740-375q-20 5-40 8t-40 3q-123 0-209.5-86.5T364-660q0-20 3-40t8-40q-78 32-126.5 102T200-480q0 116 82 198t198 82Zm-10-270Z',
    elderly: 'm320-40-64-48 104-139v-213q0-31 5-67.5t15-67.5l-60 33v142h-80v-188l176-100q25-14 43.5-21.5T494-717q25 0 45.5 21.5T587-628q32 54 58 81t56 41q11-8 19-11t19-3q25 0 43 18t18 42v420h-40v-420q0-8-6-14t-14-6q-8 0-14 6t-6 14v50h-40v-19q-54-23-84-51.5T543-557q-11 28-17.5 68.5T521-412l79 112v260h-80v-200l-71-102-9 142L320-40Zm220-700q-33 0-56.5-23.5T460-820q0-33 23.5-56.5T540-900q33 0 56.5 23.5T620-820q0 33-23.5 56.5T540-740Z',
    expand_more: 'M480-345 240-585l56-56 184 184 184-184 56 56-240 240Z',
    filter_center_focus: 'M480-360q-50 0-85-35t-35-85q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35Zm0-80q17 0 28.5-11.5T520-480q0-17-11.5-28.5T480-520q-17 0-28.5 11.5T440-480q0 17 11.5 28.5T480-440ZM200-120q-33 0-56.5-23.5T120-200v-160h80v160h160v80H200Zm400 0v-80h160v-160h80v160q0 33-23.5 56.5T760-120H600ZM120-600v-160q0-33 23.5-56.5T200-840h160v80H200v160h-80Zm640 0v-160H600v-80h160q33 0 56.5 23.5T840-760v160h-80Z',
    flash_off: 'M280-880h400l-80 280h160L643-431l-57-57 22-32h-54l-47-47 67-233H360v86l-80-80v-86ZM400-80v-320H280v-166L55-791l57-57 736 736-57 57-241-241L400-80Zm73-521Z',
    font_download: 'M256-240h84l44-122h192l44 122h84L522-720h-84L256-240Zm152-192 70-198h4l70 198H408ZM160-80q-33 0-56.5-23.5T80-160v-640q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v640q0 33-23.5 56.5T800-80H160Zm0-80h640v-640H160v640Zm0-640v640-640Z',
    format_align_left: 'M120-120v-80h720v80H120Zm0-160v-80h480v80H120Zm0-160v-80h720v80H120Zm0-160v-80h480v80H120Zm0-160v-80h720v80H120Z',
    format_color_fill: 'm247-904 57-56 343 343q23 23 23 57t-23 57L457-313q-23 23-57 23t-57-23L153-503q-23-23-23-57t23-57l190-191-96-96Zm153 153L209-560h382L400-751Zm360 471q-33 0-56.5-23.5T680-360q0-21 12.5-45t27.5-45q9-12 19-25t21-25q11 12 21 25t19 25q15 21 27.5 45t12.5 45q0 33-23.5 56.5T760-280ZM80 0v-160h800V0H80Z',
    format_color_text: 'M80 0v-160h800V0H80Zm140-280 210-560h100l210 560h-96l-50-144H368l-52 144h-96Zm176-224h168l-82-232h-4l-82 232Z',
    format_letter_spacing: 'M320-80 160-240l160-160 57 56-64 64h334l-63-64 56-56 160 160L640-80l-57-56 64-64H313l63 64-56 56ZM200-480v-400h80v400h-80Zm240 0v-400h80v400h-80Zm240 0v-400h80v400h-80Z',
    format_line_spacing: 'M240-160 80-320l56-56 64 62v-332l-64 62-56-56 160-160 160 160-56 56-64-62v332l64-62 56 56-160 160Zm240-40v-80h400v80H480Zm0-240v-80h400v80H480Zm0-240v-80h400v80H480Z',
    format_list_bulleted: 'M360-200v-80h480v80H360Zm0-240v-80h480v80H360Zm0-240v-80h480v80H360ZM200-160q-33 0-56.5-23.5T120-240q0-33 23.5-56.5T200-320q33 0 56.5 23.5T280-240q0 33-23.5 56.5T200-160Zm0-240q-33 0-56.5-23.5T120-480q0-33 23.5-56.5T200-560q33 0 56.5 23.5T280-480q0 33-23.5 56.5T200-400Zm0-240q-33 0-56.5-23.5T120-720q0-33 23.5-56.5T200-800q33 0 56.5 23.5T280-720q0 33-23.5 56.5T200-640Z',
    format_size: 'M560-160v-520H360v-120h520v120H680v520H560Zm-360 0v-320H80v-120h360v120H320v320H200Z',
    hide_image: 'm840-234-80-80v-446H314l-80-80h526q33 0 56.5 23.5T840-760v526ZM792-56l-64-64H200q-33 0-56.5-23.5T120-200v-528l-64-64 56-56 736 736-56 56ZM240-280l120-160 90 120 33-44-283-283v447h447l-80-80H240Zm297-257ZM424-424Z',
    invert_colors: 'M480-120q-133 0-226.5-92.5T160-436q0-66 25-122t69-100l226-222 226 222q44 44 69 100t25 122q0 131-93.5 223.5T480-120Zm0-80v-568L310-600q-35 33-52.5 74.5T240-436q0 97 70 166.5T480-200Z',
    keyboard: 'M160-200q-33 0-56.5-23.5T80-280v-400q0-33 23.5-56.5T160-760h640q33 0 56.5 23.5T880-680v400q0 33-23.5 56.5T800-200H160Zm0-80h640v-400H160v400Zm160-40h320v-80H320v80ZM200-440h80v-80h-80v80Zm120 0h80v-80h-80v80Zm120 0h80v-80h-80v80Zm120 0h80v-80h-80v80Zm120 0h80v-80h-80v80ZM200-560h80v-80h-80v80Zm120 0h80v-80h-80v80Zm120 0h80v-80h-80v80Zm120 0h80v-80h-80v80Zm120 0h80v-80h-80v80ZM160-280v-400 400Z',
    link: 'M440-280H280q-83 0-141.5-58.5T80-480q0-83 58.5-141.5T280-680h160v80H280q-50 0-85 35t-35 85q0 50 35 85t85 35h160v80ZM320-440v-80h320v80H320Zm200 160v-80h160q50 0 85-35t35-85q0-50-35-85t-85-35H520v-80h160q83 0 141.5 58.5T880-480q0 83-58.5 141.5T680-280H520Z',
    match_case: 'm131-252 165-440h79l165 440h-76l-39-112H247l-40 112h-76Zm139-176h131l-64-182h-4l-63 182Zm395 186q-51 0-81-27.5T554-342q0-44 34.5-72.5T677-443q23 0 45 4t38 11v-12q0-29-20.5-47T685-505q-23 0-42 9.5T610-468l-47-35q24-29 54.5-43t68.5-14q69 0 103 32.5t34 97.5v178h-63v-37h-4q-14 23-38 35t-53 12Zm12-54q35 0 59.5-24t24.5-56q-14-8-33.5-12.5T689-393q-32 0-50 14t-18 37q0 20 16 33t40 13Z',
    menu_book: 'M560-564v-68q33-14 67.5-21t72.5-7q26 0 51 4t49 10v64q-24-9-48.5-13.5T700-600q-38 0-73 9.5T560-564Zm0 220v-68q33-14 67.5-21t72.5-7q26 0 51 4t49 10v64q-24-9-48.5-13.5T700-380q-38 0-73 9t-67 27Zm0-110v-68q33-14 67.5-21t72.5-7q26 0 51 4t49 10v64q-24-9-48.5-13.5T700-490q-38 0-73 9.5T560-454ZM260-320q47 0 91.5 10.5T440-278v-394q-41-24-87-36t-93-12q-36 0-71.5 7T120-692v396q35-12 69.5-18t70.5-6Zm260 42q44-21 88.5-31.5T700-320q36 0 70.5 6t69.5 18v-396q-33-14-68.5-21t-71.5-7q-47 0-93 12t-87 36v394Zm-40 118q-48-38-104-59t-116-21q-42 0-82.5 11T100-198q-21 11-40.5-1T40-234v-482q0-11 5.5-21T62-752q46-24 96-36t102-12q58 0 113.5 15T480-740q51-30 106.5-45T700-800q52 0 102 12t96 36q11 5 16.5 15t5.5 21v482q0 23-19.5 35t-40.5 1q-37-20-77.5-31T700-240q-60 0-116 21t-104 59ZM280-494Z',
    motion_photos_paused: 'M360-360h80v-240h-80v240Zm160 0h80v-240h-80v240ZM324-111.5Q251-143 197-197t-85.5-127Q80-397 80-480q0-43 9-84.5t26-80.5l62 62q-8 26-12.5 51.5T160-480q0 134 93 227t227 93q134 0 227-93t93-227q0-134-93-227t-227-93q-27 0-52.5 4.5T377-783l-61-61q40-18 80-27t84-9q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80q-83 0-156-31.5Zm-146.5-586Q160-715 160-740t17.5-42.5Q195-800 220-800t42.5 17.5Q280-765 280-740t-17.5 42.5Q245-680 220-680t-42.5-17.5ZM480-480Z',
    palette: 'M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 32.5-156t88-127Q256-817 330-848.5T488-880q80 0 151 27.5t124.5 76q53.5 48.5 85 115T880-518q0 115-70 176.5T640-280h-74q-9 0-12.5 5t-3.5 11q0 12 15 34.5t15 51.5q0 50-27.5 74T480-80Zm0-400Zm-220 40q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm120-160q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm200 0q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm120 160q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17ZM480-160q9 0 14.5-5t5.5-13q0-14-15-33t-15-57q0-42 29-67t71-25h70q66 0 113-38.5T800-518q0-121-92.5-201.5T488-800q-136 0-232 93t-96 227q0 133 93.5 226.5T480-160Z',
    pause: 'M520-200v-560h240v560H520Zm-320 0v-560h240v560H200Zm400-80h80v-400h-80v400Zm-320 0h80v-400h-80v400Zm0-400v400-400Zm320 0v400-400Z',
    play_arrow: 'M320-200v-560l440 280-440 280Zm80-280Zm0 134 210-134-210-134v268Z',
    record_voice_over: 'm798-322-62-62q44-41 69-97t25-119q0-63-25-118t-69-96l62-64q56 53 89 125t33 153q0 81-33 153t-89 125ZM670-450l-64-64q18-17 29-38.5t11-47.5q0-26-11-47.5T606-686l64-64q32 29 50 67.5t18 82.5q0 44-18 82.5T670-450Zm-310 10q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47ZM40-120v-112q0-33 17-62t47-44q51-26 115-44t141-18q77 0 141 18t115 44q30 15 47 44t17 62v112H40Zm80-80h480v-32q0-11-5.5-20T580-266q-36-18-92.5-36T360-320q-71 0-127.5 18T140-266q-9 5-14.5 14t-5.5 20v32Zm240-320q33 0 56.5-23.5T440-600q0-33-23.5-56.5T360-680q-33 0-56.5 23.5T280-600q0 33 23.5 56.5T360-520Zm0-80Zm0 400Z',
    remove: 'M200-440v-80h560v80H200Z',
    space_bar: 'M160-360v-240h80v160h480v-160h80v240H160Z',
    speed: 'M418-340q24 24 62 23.5t56-27.5l224-336-336 224q-27 18-28.5 55t22.5 61Zm62-460q59 0 113.5 16.5T696-734l-76 48q-33-17-68.5-25.5T480-720q-133 0-226.5 93.5T160-400q0 42 11.5 83t32.5 77h552q23-38 33.5-79t10.5-85q0-36-8.5-70T766-540l48-76q30 47 47.5 100T880-406q1 57-13 109t-41 99q-11 18-30 28t-40 10H204q-21 0-40-10t-30-28q-26-45-40-95.5T80-400q0-83 31.5-155.5t86-127Q252-737 325-768.5T480-800Zm7 313Z',
    stop: 'M320-640v320-320Zm-80 400v-480h480v480H240Zm80-80h320v-320H320v320Z',
    swap_horiz: 'M280-160 80-360l200-200 56 57-103 103h287v80H233l103 103-56 57Zm400-240-56-57 103-103H440v-80h287L624-743l56-57 200 200-200 200Z',
    title: 'M420-160v-520H200v-120h560v120H540v520H420Z',
    toc: 'M120-280v-80h560v80H120Zm0-160v-80h560v80H120Zm0-160v-80h560v80H120Zm680 320q-17 0-28.5-11.5T760-320q0-17 11.5-28.5T800-360q17 0 28.5 11.5T840-320q0 17-11.5 28.5T800-280Zm0-160q-17 0-28.5-11.5T760-480q0-17 11.5-28.5T800-520q17 0 28.5 11.5T840-480q0 17-11.5 28.5T800-440Zm0-160q-17 0-28.5-11.5T760-640q0-17 11.5-28.5T800-680q17 0 28.5 11.5T840-640q0 17-11.5 28.5T800-600Z',
    tooltip: 'M480-80 373-240H160q-33 0-56.5-23.5T80-320v-480q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H587L480-80Zm0-144 64-96h256v-480H160v480h256l64 96Zm0-336Z',
    view_day: 'M120-160v-80h720v80H120Zm0-560v-80h720v80H120Zm80 400q-33 0-56.5-23.5T120-400v-160q0-33 23.5-56.5T200-640h560q33 0 56.5 23.5T840-560v160q0 33-23.5 56.5T760-320H200Zm0-80h560v-160H200v160Zm0-160v160-160Z',
    visibility: 'M607.5-372.5Q660-425 660-500t-52.5-127.5Q555-680 480-680t-127.5 52.5Q300-575 300-500t52.5 127.5Q405-320 480-320t127.5-52.5Zm-204-51Q372-455 372-500t31.5-76.5Q435-608 480-608t76.5 31.5Q588-545 588-500t-31.5 76.5Q525-392 480-392t-76.5-31.5ZM214-281.5Q94-363 40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200q-146 0-266-81.5ZM480-500Zm207.5 160.5Q782-399 832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280q113 0 207.5-59.5Z',
    volume_off: 'M792-56 671-177q-25 16-53 27.5T560-131v-82q14-5 27.5-10t25.5-12L480-368v208L280-360H120v-240h128L56-792l56-56 736 736-56 56Zm-8-232-58-58q17-31 25.5-65t8.5-70q0-94-55-168T560-749v-82q124 28 202 125.5T840-481q0 53-14.5 102T784-288ZM650-422l-90-90v-130q47 22 73.5 66t26.5 96q0 15-2.5 29.5T650-422ZM480-592 376-696l104-104v208Zm-80 238v-94l-72-72H200v80h114l86 86Zm-36-130Z',
    zoom_in: 'M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Zm-40-60v-80h-80v-80h80v-80h80v80h80v80h-80v80h-80Z'
  };
  function svg(name, cls) {
    return '<svg' + (cls ? ' class="' + cls + '"' : '') + ' viewBox="0 -960 960 960" aria-hidden="true" focusable="false"><path d="' +
      ICONS[name] + '"/></svg>';
  }

  /* ---------- build DOM ---------- */
  /* A <style> with the CSP nonce, if there is one. A policy that blocks it
     gives it no sheet. */
  function styleEl(css, parent, id) {
    var el = document.createElement('style');
    if (id) el.id = id;
    if (CFG.nonce) el.setAttribute('nonce', CFG.nonce);
    el.textContent = css;
    parent.appendChild(el);
    return el;
  }
  /* Takes out everything build() added. Used when the site's Content
     Security Policy blocks our styles: an unstyled panel, or effects with no
     panel to undo them, would be worse than no toolbar. */
  function unbuild() {
    [effectsEl, guideEl, maskEl, tipEl, markEl, host].forEach(function (n) {
      if (n && n.parentNode) n.parentNode.removeChild(n);
    });
    effectsEl = guideEl = maskEl = tipEl = markEl = host = shadow = null;
    warn("the site's Content Security Policy blocked the toolbar's styles, so it did not load. " +
      "Allow style-src 'unsafe-inline', or pass the policy's nonce in data-nonce.");
    return false;
  }

  function build() {
    effectsEl = styleEl(EFFECTS, document.head, 'a11y-toolbar-effects');
    if (!effectsEl.sheet) return unbuild();

    guideEl = document.createElement('div');
    guideEl.id = GUIDE_ID;
    guideEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(guideEl);

    maskEl = document.createElement('div');
    maskEl.id = MASK_ID;
    maskEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(maskEl);

    tipEl = document.createElement('div');
    tipEl.id = TIP_ID;
    tipEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(tipEl);

    markEl = document.createElement('div');
    markEl.id = MARK_ID;
    markEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(markEl);

    host = document.createElement('div');
    host.id = HOST_ID;
    document.body.appendChild(host);
    shadow = host.attachShadow({ mode: 'open' });

    /* Same policy, same nonce: if the effects sheet got through, so does
       this one. */
    styleEl(panelCSS(), shadow);

    /* Every string below is a constant from this file. The one value that comes
       from the host page, the statement URL, is set through the DOM further down. */
    var wrap = document.createElement('div');
    wrap.className = 'wrap';
    wrap.innerHTML =
      '<button class="launch" type="button" aria-label="Accessibility Options" aria-expanded="false" aria-controls="a11y-panel">' +
        svg('accessibility_new') +
      '</button>' +
      '<button class="restore" type="button" aria-label="Show accessibility options">' +
        '<span class="tab">' + svg('accessibility_new') + '</span>' +
      '</button>' +
      /* Non-modal dialog: the page behind is not inert, so aria-modal is false.
         Claiming modality would also license AT to ignore the live region,
         which lives inside the panel below. */
      '<div class="panel" id="a11y-panel" role="dialog" aria-modal="false" aria-labelledby="a11y-title" hidden>' +
        '<div class="hd">' +
          '<div class="hd-top"><h2 id="a11y-title">Accessibility Options</h2>' +
          '<button class="x" type="button" aria-label="Close">' + svg('close') + '</button></div>' +
          '<p class="hint">Alt+A opens and closes. Esc closes.</p>' +
          '<button class="skip" type="button" data-act="skip">Skip to main content</button>' +
        '</div>' +
        /* Firefox makes any scrolling box a Tab stop, which here would be an
           unnamed stop between Skip and Profiles. Every row in it can take
           focus, and focus scrolls it, so it does not need to be one. */
        '<div class="body" tabindex="-1"><div class="skip-row"></div>' + SECTIONS.map(section).join('') + mailRow() + '</div>' +
        '<div class="ft">' +
          '<p class="note ft-save">Saved on this device only. Alt+Shift+A resets all.</p>' +
          '<div class="ft-row">' +
            '<button type="button" data-act="reset">Reset all</button>' +
            '<button type="button" data-act="hide" aria-describedby="a11y-hide-d">' + HIDE.wide[0] + '</button>' +
          '</div>' +
          '<p class="note ft-tab" id="a11y-hide-d">' + HIDE.wide[1] + '</p>' +
          '<div class="sr" role="status" aria-live="polite" data-live></div>' +
        '</div>' +
      '</div>';

    shadow.appendChild(wrap);

    wrapEl = wrap;
    toggleBtn = shadow.querySelector('.launch');
    restoreBtn = shadow.querySelector('.restore');
    panel = shadow.querySelector('.panel');
    liveRegion = shadow.querySelector('[data-live]');

    if (CFG.statementUrl && !/^\s*javascript:/i.test(CFG.statementUrl)) {
      var link = document.createElement('a');
      link.setAttribute('href', CFG.statementUrl);
      link.setAttribute('aria-label', 'Accessibility statement');
      link.textContent = 'Statement';
      shadow.querySelector('.ft-row').appendChild(link);
    }

    toggleBtn.addEventListener('click', function () { panelOpen ? close() : open(); });
    restoreBtn.addEventListener('click', restore);
    shadow.querySelector('.x').addEventListener('click', function () { close(); });
    shadow.querySelector('[data-act="reset"]').addEventListener('click', resetAll);
    shadow.querySelector('[data-act="hide"]').addEventListener('click', hideForNow);
    shadow.querySelector('[data-act="skip"]').addEventListener('click', skipToMain);

    shadow.querySelectorAll('.sec-btn').forEach(function (b) {
      b.addEventListener('click', function () {
        var open = b.getAttribute('aria-expanded') !== 'true';
        b.setAttribute('aria-expanded', open ? 'true' : 'false');
        shadow.getElementById(b.getAttribute('aria-controls')).hidden = !open;
      });
    });

    shadow.querySelectorAll('.step button').forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.getAttribute('aria-disabled') === 'true') return;
        var group = b.parentNode.getAttribute('data-step');
        setStep(group, state[group] + Number(b.getAttribute('data-dir')));
      });
    });
    shadow.querySelectorAll('.val').forEach(function (v) {
      v.addEventListener('keydown', function (e) {
        var group = v.getAttribute('data-out');
        var next = { ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1 }[e.key];
        if (next) next = state[group] + next;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = MAXES[group];
        else return;
        e.preventDefault();
        setStep(group, next);
      });
    });

    shadow.querySelectorAll('.sw').forEach(function (b) {
      b.addEventListener('click', function () { flip(b); });
    });
    shadow.querySelectorAll('.seg button').forEach(function (b) {
      b.addEventListener('click', function () { choose(b); });
    });
    /* While the picker is being dragged only the colours are redrawn; the full
       apply, save and resync wait for the visitor to settle on one. */
    shadow.querySelectorAll('.clr input').forEach(function (inp) {
      inp.addEventListener('input', function () { setColor(inp.getAttribute('data-key'), inp.value, false); });
      inp.addEventListener('change', function () { setColor(inp.getAttribute('data-key'), inp.value, true); });
    });
    shadow.querySelectorAll('.rst').forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.getAttribute('aria-disabled') === 'true') return;
        setColor(b.getAttribute('data-key'), '', true);
        announce(rowLabel(b) + ' reset');
      });
    });

    /* Read aloud. The selection is taken on pointerdown, before the click
       can disturb it. */
    shadow.querySelectorAll('[data-read]').forEach(function (b) {
      if (b.getAttribute('data-read') === 'play') {
        b.addEventListener('pointerdown', function () { picked = selectedText(); });
      }
      b.addEventListener('click', function () {
        if (b.disabled || b.getAttribute('aria-disabled') === 'true') return;
        ({ play: readPlay, pause: readPause, stop: readStop })[b.getAttribute('data-read')]();
      });
    });
    window.addEventListener('pagehide', function () { if (reader.status !== 'idle') readStop(); });
    window.addEventListener('scroll', placeMark, { passive: true, capture: true });
    window.addEventListener('resize', placeMark, { passive: true });

    /* The address goes in as text, put together from two parts (D-31). */
    addrEl = shadow.getElementById('a11y-mail-a');
    addrEl.textContent = reportAddress();
    copyBtn = shadow.querySelector('[data-act="copy"]');
    copyBtn.addEventListener('click', copyAddress);

    phoneLayout();
    if (PHONE && PHONE.addEventListener) PHONE.addEventListener('change', phoneLayout);
    else if (PHONE && PHONE.addListener) PHONE.addListener(phoneLayout);
    window.addEventListener('resize', fitAids, { passive: true });

    /* Escape is bound at document level, not on the panel: as a non-modal dialog
       the panel can be open while focus sits out on the page. */
    document.addEventListener('keydown', function (e) {
      var isA = e.code ? e.code === 'KeyA' : /^a$/i.test(e.key || '');
      /* Safety hatch. Registered unconditionally, even when CFG.hotkey is false,
         so a visitor can always undo a preference that hid what they needed,
         including the launcher itself. e.code, because Alt and Shift change
         e.key on many layouts (Option+A is "å" on a Mac). */
      if (e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey && isA) {
        e.preventDefault();
        resetAll();
        return;
      }
      if (e.key === 'Escape' && panelOpen) { close(); return; }
      /* Alt+A also works when the launcher is hidden: that is the only way back. */
      if ((CFG.hotkey || launcherHidden()) && e.altKey && !e.shiftKey && !e.ctrlKey && !e.metaKey && isA) {
        e.preventDefault();
        panelOpen ? close() : open();
        return;
      }
      if (e.key === 'Tab' && panelOpen) cycle(e);
    });

    /* Clicking outside deliberately does NOT close the panel: a misclick on the
       page should not throw away the visitor's place (phase 1 spec, D-20). */

    document.addEventListener('mousemove', moveGuide, { passive: true });
    document.addEventListener('touchmove', moveGuide, { passive: true });
    document.addEventListener('focusin', maskToFocus, { passive: true });
    document.addEventListener('mouseover', showTip, { passive: true });
    document.addEventListener('focusin', showTip, { passive: true });
    document.addEventListener('mouseout', hideTip, { passive: true });
    document.addEventListener('focusout', hideTip, { passive: true });

    if (darkQuery && darkQuery.addEventListener) {
      darkQuery.addEventListener('change', function () { if (state.contrast === 'auto') apply(); });
    }
    return true;
  }

  function section(s) {
    var rows = s.rows.filter(function (r) { return !r.filter || CFG.filterEffects; });
    var id = 'a11y-sec-' + s.id;
    return '<div class="sec" data-sec="' + s.id + '">' +
      '<h3><button class="sec-btn" type="button" aria-expanded="true" aria-controls="' + id + '">' +
        '<span>' + s.title + '</span>' + svg('expand_more') + '</button></h3>' +
      '<div class="sec-body" id="' + id + '">' + rows.map(row).join('') + '</div></div>';
  }

  var uid = 0;
  function row(r) {
    var id = 'a11y-c' + (++uid), lid = id + '-l', did = id + '-d', lbl, ctl;
    var off = r.speech && !SPEECH;
    var sub = off ? 'Not available in this browser' : r.sub;
    /* The second line describes the control; it is not part of its name. */
    var desc = sub ? ' aria-describedby="' + did + '"' : '';
    if (r.type === 'switch') {
      /* A <label for> on a button names it and makes the label text clickable. */
      lbl = '<label class="lbl" id="' + lid + '" for="' + id + '">' + r.label + '</label>';
      ctl = '<button class="sw" type="button" role="switch" aria-checked="false" id="' + id +
        '" aria-labelledby="' + lid + '"' + desc + ' data-key="' + r.key + '"' +
        (r.value !== undefined ? ' data-value="' + r.value + '"' : '') + '></button>';
    } else if (r.type === 'read') {
      lbl = '<span class="lbl" id="' + lid + '">' + r.label + '</span>';
      ctl = '<div class="rd" role="group" aria-labelledby="' + lid + '"' + desc + '>' +
        [['play', 'play_arrow', 'Play'], ['pause', 'pause', 'Pause'], ['stop', 'stop', 'Stop']].map(function (b) {
          return '<button type="button" data-read="' + b[0] + '"' + (off ? ' disabled' : ' aria-disabled="' + (b[0] === 'play' ? 'false' : 'true') + '"') + '>' +
            svg(b[1]) + '<span>' + b[2] + '</span></button>';
        }).join('') + '</div>';
    } else if (r.type === 'color') {
      /* The Reset button's name keeps its visible word first (SC 2.5.3). */
      lbl = '<label class="lbl" id="' + lid + '" for="' + id + '">' + r.label + '</label>';
      ctl = '<div class="clr"><input type="color" id="' + id + '" data-key="' + r.key + '" value="' + SWATCH[r.key] + '">' +
        '<button class="rst" type="button" data-key="' + r.key + '" aria-label="Reset ' + r.label.toLowerCase() +
        '" aria-disabled="true">Reset</button></div>';
    } else {
      lbl = '<span class="lbl" id="' + lid + '">' + r.label + '</span>';
      ctl = r.type === 'step' ? stepper(r, lid) : choice(r, lid, desc, off);
    }
    if (sub) lbl = '<span class="txt">' + lbl + '<span class="sub" id="' + did + '">' + sub + '</span></span>';
    return '<div class="row">' + svg(r.icon, 'ic') + lbl + '<div class="ctl">' + ctl + '</div></div>' +
      (r.list ? '<div class="list" data-' + r.list + ' hidden></div>' : '');
  }
  function stepper(r, lid) {
    return '<div class="step" data-step="' + r.key + '">' +
      '<button type="button" data-dir="-1" aria-label="Decrease ' + r.say + '">' + svg('remove') + '</button>' +
      '<span class="val" role="spinbutton" tabindex="0" aria-labelledby="' + lid + '" aria-valuemin="0" aria-valuemax="' +
        MAXES[r.key] + '" aria-valuenow="0" aria-valuetext="' + OUT[r.key][0] + '" aria-live="polite" data-out="' + r.key + '">' +
        OUT[r.key][0] + '</span>' +
      '<button type="button" data-dir="1" aria-label="Increase ' + r.say + '">' + svg('add') + '</button></div>';
  }
  function choice(r, lid, desc, off) {
    return '<div class="seg" role="group" aria-labelledby="' + lid + '"' + (desc || '') + '>' + r.options.map(function (o) {
      return '<button type="button" aria-pressed="false" data-key="' + r.key + '" data-value="' + o[0] + '"' +
        (off ? ' disabled' : '') + '>' + o[1] + '</button>';
    }).join('') + '</div>';
  }
  /* The only way to report a problem: the address to write to, and a Copy
     button (D-30, D-31). No email link: one does nothing for a visitor who
     uses email in the browser. The markup holds no address; build() puts it
     in as text. The Copy button is described by the address, so a screen
     reader says what it copies. */
  function mailRow() {
    return '<div class="mail">' +
      '<h3>Report a problem</h3>' +
      '<p class="note">Email us about anything that does not work for you.</p>' +
      '<div class="addr-row">' +
        '<code class="addr" id="a11y-mail-a"></code>' +
        '<button class="copy" type="button" data-act="copy" aria-describedby="a11y-mail-a">' + svg('content_copy') + '<span>Copy</span></button>' +
      '</div></div>';
  }
  /* The footer's Hide button and the line under it, on a computer and, in
     the sheet, on a phone (D-31, D-32). */
  var HIDE = {
    wide: ['Hide until I close this tab', 'A small tab on the edge brings it back.'],
    phone: ['Hide', 'A tab on the edge brings it back.']
  };
  var PHONE = window.matchMedia ? window.matchMedia('(max-width:479px)') : null;
  /* Under 480px Skip to main content moves from the header into the first
     row of the body, so the header is one line, and Hide gets its short
     label. Moved, not copied: there is one Skip button, in the same place
     in the Tab order. Runs again when the width crosses 480px. */
  function phoneLayout() {
    var on = !!(PHONE && PHONE.matches);
    var skip = shadow.querySelector('[data-act="skip"]');
    var to = shadow.querySelector(on ? '.skip-row' : '.hd');
    if (skip.parentNode !== to) {
      var had = shadow.activeElement === skip;
      to.appendChild(skip);
      if (had) skip.focus();
    }
    var words = on ? HIDE.phone : HIDE.wide;
    shadow.querySelector('[data-act="hide"]').textContent = words[0];
    shadow.getElementById('a11y-hide-d').textContent = words[1];
  }
  function rowLabel(el) {
    var id = el.getAttribute('aria-labelledby');
    var l = id ? shadow.getElementById(id) : el.closest('.row').querySelector('.lbl');
    return l ? l.textContent : '';
  }

  /* ---------- control handlers ----------
     Changing a row by hand leaves an active profile on (phase 3): the
     profile only undoes, when turned off, what still holds its value. */
  function setStep(group, next) {
    next = Math.min(MAXES[group], Math.max(0, next));
    if (next === state[group]) return;
    state[group] = next;
    commit(); /* the readout is its own live region */
  }

  function flip(b) {
    var key = b.getAttribute('data-key');
    var raw = b.getAttribute('data-value');
    if (key === 'profile') {
      var p = profileById(raw);
      if (!p) return;
      var wasOn = state.profile === raw;
      /* One profile at a time: the previous one is turned off first, so its
         settings are undone before the next one records its own. */
      profileOff();
      if (!wasOn) profileOn(p);
      commit('Profile ' + rowLabel(b) + (wasOn ? ' off' : ' on'));
      return;
    }
    var msg = '';
    if (raw === null) {
      state[key] = !state[key];
      var other = EXCLUSIVE[key];
      if (state[key] && other && state[other]) {
        state[other] = false;
        msg = switchLabel(other) + ' turned off';
      }
    } else {
      var v = typeof DEFAULTS[key] === 'number' ? Number(raw) : raw;
      state[key] = state[key] === v ? DEFAULTS[key] : v;
    }
    commit(msg); /* role=switch announces its own new state */
  }
  function switchLabel(key) {
    var s = shadow.querySelector('.sw[data-key="' + key + '"]:not([data-value])');
    return s ? rowLabel(s) : key;
  }

  function choose(b) {
    var key = b.getAttribute('data-key');
    var raw = b.getAttribute('data-value');
    if (key === 'dock') {
      state.dock = raw;
      commit(raw === 'hidden' ? 'Button hidden. A small tab on the edge brings it back.' : '');
      return;
    }
    state[key] = typeof DEFAULTS[key] === 'number' ? Number(raw) : raw;
    commit();
  }

  /* done=false while the picker is still moving: redraw the colours only. */
  function setColor(key, value, done) {
    state[key] = HEX6.test(value) ? value.toLowerCase() : '';
    if (done) commit();
    else applyColors();
  }

  /* Focuses the page's <main>, or its first h1 if there is no <main>. Like
     Page outline (D-08) the only thing it adds is tabindex="-1", so a
     non-interactive target can take focus. The panel closes first: on a
     phone it covers the content the visitor asked to go to. */
  function skipToMain() {
    var t = firstVisible('main,[role="main"]') || firstVisible('h1');
    if (!t) { announce('No main content found on this page.'); return; }
    close(true);
    if (!t.hasAttribute('tabindex')) t.setAttribute('tabindex', '-1');
    t.focus({ preventScroll: true });
    if (t.scrollIntoView) t.scrollIntoView({ block: 'start' });
  }
  function firstVisible(sel) {
    var nodes = document.querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) if (visible(nodes[i])) return nodes[i];
    return null;
  }

  function hideForNow() {
    hiddenNow = true;
    saveHidden();
    apply();
    close();
  }

  function launcherHidden() {
    return state.dock === 'hidden' || hiddenNow;
  }

  /* The restore tab: brings the launcher back, whichever way it was
     hidden, and opens the panel. On a phone there is no Alt+A, so without
     this a hidden launcher would leave no way back (D-07, D-31). */
  function restore() {
    hiddenNow = false;
    saveHidden();
    if (state.dock === 'hidden') state.dock = 'default';
    commit();
    open();
  }

  /* commit = apply + persist + resync + announce, in that order */
  function commit(msg) {
    apply(); save(); sync();
    if (msg) announce(msg);
  }

  /* ---------- open / close / announce ---------- */
  function focusables() {
    return Array.prototype.filter.call(
      panel.querySelectorAll('button,a[href],input,select,textarea,[tabindex="0"]'),
      function (el) { return !el.disabled && !el.closest('[hidden]'); }
    );
  }
  /* Tab wraps around inside the panel while focus is in it (phase 1 spec,
     D-20). It is not a trap: the page stays clickable, and Escape, Close and
     Alt+A all leave. If focus is already out on the page, Tab is left alone. */
  function cycle(e) {
    var a = shadow.activeElement;
    if (!a || !panel.contains(a)) return;
    var f = focusables();
    if (!f.length) return;
    if (e.shiftKey && a === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
    else if (!e.shiftKey && a === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
  }
  /* `open` on the wrapper hides the launcher and the restore tab while the
     phone sheet is up (D-32); on a computer the card covers the launcher
     anyway, and the class does nothing. */
  function open() {
    panel.hidden = false;
    wrapEl.classList.add('open');
    toggleBtn.setAttribute('aria-expanded', 'true');
    panelOpen = true;
    sync();
    watchDom();
    fitAids();
    shadow.querySelector('.x').focus();
  }
  /* Focus returns to the launcher, or to the restore tab that stands in for
     it while it is hidden (D-31). With toPage the caller is about to move
     focus into the page itself. */
  function close(toPage) {
    panel.hidden = true;
    wrapEl.classList.remove('open');
    toggleBtn.setAttribute('aria-expanded', 'false');
    panelOpen = false;
    watchDom();
    if (toPage === true) return;
    (launcherHidden() ? restoreBtn : toggleBtn).focus();
  }
  function announce(msg) {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(function () { liveRegion.textContent = msg; }, 60);
  }

  /* ---------- reading guide and reading mask ----------
     While the phone sheet is open (D-32) both stay on the page above it: a
     finger scrolling the sheet does not move them, and a finger dragged
     down into the sheet stops them at its top edge. On a computer they
     follow the pointer as they always have. */
  function moveGuide(e) {
    if (!state.guide && !state.mask) return;
    var top = sheetTop();
    if (top !== null && e.target === host) return;
    var y = e.touches ? e.touches[0].clientY : e.clientY;
    if (state.guide && guideEl) guideEl.style.top = Math.max(0, aboveSheet(y - 21, guideEl.offsetHeight, top)) + 'px';
    if (state.mask && maskEl) maskEl.style.top = aboveSheet(y, maskEl.offsetHeight / 2, top) + 'px';
  }
  /* Keyboard users move by focus, not by pointer, so the mask's band also
     centres on whatever takes focus out on the page. Focus inside the panel
     arrives retargeted to the host and is ignored. */
  function maskToFocus(e) {
    if (!state.mask || !maskEl) return;
    var t = e.target;
    if (!t || t === host || t === document.body || !t.getBoundingClientRect) return;
    var r = t.getBoundingClientRect();
    maskEl.style.top = Math.round(aboveSheet(r.top + r.height / 2, maskEl.offsetHeight / 2, sheetTop())) + 'px';
  }
  /* The top edge of the phone sheet, or null when there is none: the panel
     is closed, or it is the computer card, which does not span the screen. */
  function sheetTop() {
    if (!panelOpen || !panel) return null;
    var r = panel.getBoundingClientRect();
    return r.top > 0 && r.bottom >= window.innerHeight - 1 && r.width >= root.clientWidth - 1 ? r.top : null;
  }
  /* y, moved up if need be so that `below` pixels under it still clear the
     sheet. */
  function aboveSheet(y, below, top) {
    return top === null ? y : Math.min(y, top - below);
  }
  /* When the sheet opens, or the guide or mask is turned on under it, one
     that would sit behind the sheet moves to the middle of the page above. */
  function fitAids() {
    var top = sheetTop();
    if (top === null) return;
    if (state.guide && guideEl && guideEl.getBoundingClientRect().bottom > top) {
      guideEl.style.top = Math.max(0, Math.round(top / 2 - guideEl.offsetHeight / 2)) + 'px';
    }
    if (state.mask && maskEl && maskEl.getBoundingClientRect().bottom > top) {
      maskEl.style.top = Math.round(top / 2) + 'px';
    }
  }

  /* ---------- tooltips ----------
     Surfaces an existing title/aria-label visually. It never invents a label and
     never writes to the DOM, so it cannot change what AT reports. */
  function tipTarget(el) {
    while (el && el !== document.body) {
      if (el === host || el.id === HOST_ID) return null;
      if (el.nodeType === 1 && (el.getAttribute('title') || el.getAttribute('aria-label'))) return el;
      el = el.parentNode;
    }
    return null;
  }
  function showTip(e) {
    if (!state.tooltips || !tipEl) return;
    var el = tipTarget(e.target);
    if (!el) return;
    var text = el.getAttribute('title') || el.getAttribute('aria-label');
    if (!text) return;
    tipEl.textContent = text;
    tipEl.classList.add('on');
    var r = el.getBoundingClientRect();
    var top = r.bottom + 8;
    if (top + 40 > window.innerHeight) top = Math.max(4, r.top - 40);
    tipEl.style.top = top + 'px';
    tipEl.style.left = Math.max(4, Math.min(r.left, window.innerWidth - 240)) + 'px';
  }
  function hideTip() {
    if (tipEl) tipEl.classList.remove('on');
  }

  /* ---------- page structure ----------
     A read-only index of what is already in the page. It adds no ARIA and
     relabels nothing; if a heading is empty or a landmark unlabelled, that shows
     up here as a gap, which is a finding for scan.mjs rather than something to
     paper over. */
  /* Deliberately not offsetParent: that is null for any position:fixed element,
     so a sticky header's headings would silently vanish from this list. */
  function visible(el) {
    if (el.hidden) return false;
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ checkOpacity: false, checkVisibilityCSS: true });
    }
    var cs = window.getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden';
  }

  function structureItems() {
    var out = [];
    var sel = 'h1,h2,h3,h4,h5,h6,main,nav,[role="main"],[role="navigation"],[role="search"],form';
    var nodes = document.querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (host && (el === host || host.contains(el))) continue;
      if (!visible(el)) continue;
      var tag = el.tagName.toLowerCase();
      var isHeading = /^h[1-6]$/.test(tag);
      var label = (el.getAttribute('aria-label') || '').trim();
      if (!label && isHeading) label = (el.textContent || '').trim().replace(/\s+/g, ' ');
      if (!label) label = { main: 'Main content', nav: 'Navigation', form: 'Form' }[tag] || tag;
      out.push({ el: el, lvl: isHeading ? tag.toUpperCase() : 'REGION', label: label.slice(0, 90) || '(empty)' });
    }
    return out;
  }

  /* All links: the same read-only index, of every visible <a href>. The name
     is the link's own text; a link with none shows its href, which is a
     finding for scan.mjs, not something to guess a better label for. A link
     can already take focus, so this adds nothing to it at all. */
  function linkItems() {
    var out = [];
    var nodes = document.querySelectorAll('a[href]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!visible(el)) continue;
      var label = (el.textContent || '').trim().replace(/\s+/g, ' ') || el.getAttribute('href');
      out.push({ el: el, label: label.slice(0, 90), link: true });
    }
    return out;
  }

  var LISTS = [
    { key: 'structure', attr: 'data-struct', items: structureItems, none: 'No headings or landmarks found on this page.' },
    { key: 'linkList', attr: 'data-links', items: linkItems, none: 'No links found on this page.' }
  ];
  function renderLists() {
    LISTS.forEach(renderList);
  }
  function renderList(def) {
    var box = shadow.querySelector('[' + def.attr + ']');
    if (!box) return;
    box.hidden = !state[def.key];
    if (!state[def.key]) { box.innerHTML = ''; return; }
    var items = def.items();
    if (!items.length) {
      box.innerHTML = '';
      var p = document.createElement('p');
      p.className = 'empty';
      p.textContent = def.none;
      box.appendChild(p);
      return;
    }
    var ul = document.createElement('ul');
    items.forEach(function (it) {
      var li = document.createElement('li');
      var b = document.createElement('button');
      var tx = document.createElement('span');
      b.type = 'button';
      if (it.lvl) {
        var lvl = document.createElement('span');
        lvl.className = 'lvl';
        lvl.textContent = it.lvl;
        /* the space keeps "H2 Services" apart in the accessible name; a flex
           container does not render it */
        b.appendChild(lvl);
        b.appendChild(document.createTextNode(' '));
      }
      tx.textContent = it.label;
      b.appendChild(tx);
      b.addEventListener('click', function () {
        if (!it.link && !it.el.hasAttribute('tabindex')) it.el.setAttribute('tabindex', '-1');
        it.el.focus({ preventScroll: true });
        if (it.el.scrollIntoView) it.el.scrollIntoView({ block: it.link ? 'center' : 'start' });
        announce('Moved to ' + it.label);
      });
      li.appendChild(b);
      ul.appendChild(li);
    });
    box.innerHTML = '';
    box.appendChild(ul);
  }

  /* ---------- read aloud (D-16) ----------
     A reading aid: the browser's own speechSynthesis reads the visitor's
     selection, or the page's main content, block by block, with a ring
     round the block being read. It is not a screen reader and never
     imitates one: it reads text only, announces no controls, roles or
     states, and never moves focus. Nothing is spoken until Play is pressed. */
  var reader = { status: 'idle', queue: [], i: 0, run: 0, utt: null, el: null };
  var picked = '';
  /* Never read: page chrome, code, hidden text, form fields, and our own nodes. */
  var NO_READ = { NAV: 1, FOOTER: 1, ASIDE: 1, SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, SVG: 1, IFRAME: 1,
    CANVAS: 1, VIDEO: 1, AUDIO: 1, OBJECT: 1, SELECT: 1, TEXTAREA: 1 };
  var BLOCK = /^(P|H[1-6]|LI|DT|DD|BLOCKQUOTE|PRE|FIGCAPTION|CAPTION|TD|TH|SUMMARY|LEGEND|DIV|SECTION|ARTICLE|HEADER|MAIN|FORM|FIELDSET|FIGURE|ADDRESS|DETAILS|DIALOG|BODY)$/;

  function selectedText() {
    var s = window.getSelection ? window.getSelection() : null;
    if (!s || s.isCollapsed || !s.rangeCount) return '';
    if (host && (s.anchorNode === host || host.contains(s.anchorNode))) return '';
    return String(s).trim();
  }
  function mine(el) {
    return el === host || el === guideEl || el === maskEl || el === tipEl || el === markEl || el === effectsEl;
  }

  /* The text of `top`, grouped by the nearest block element around each
     piece, in reading order. Read-only: it never writes to the page. */
  function readBlocks(top) {
    var out = [], last = null;
    var walker = document.createTreeWalker(top, 5 /* SHOW_ELEMENT | SHOW_TEXT */, { acceptNode: function (n) {
      if (n.nodeType === 3) return n.nodeValue.trim() ? 1 /* ACCEPT */ : 3 /* SKIP */;
      if (NO_READ[n.nodeName.toUpperCase()] || mine(n) || n.getAttribute('aria-hidden') === 'true' || !visible(n)) return 2; /* REJECT */
      return 3;
    } });
    for (var t = walker.nextNode(); t; t = walker.nextNode()) {
      var b = t.parentNode;
      while (b !== top && !BLOCK.test(b.nodeName.toUpperCase())) b = b.parentNode;
      var text = t.nodeValue.replace(/\s+/g, ' ');
      if (last && last.el === b) last.text += text;
      else out.push(last = { el: b, text: text });
    }
    return out.filter(function (o) { return (o.text = o.text.trim()); });
  }
  /* Long blocks go to the speech engine a sentence or so at a time: some
     engines stop short on a long utterance. */
  function chunks(text) {
    var parts = text.match(/[^.!?]+[.!?]+["')\]]*\s*|[^.!?]+$/g) || [text];
    var out = [], cur = '';
    parts.forEach(function (p) {
      if (cur && (cur + p).length > 220) { out.push(cur.trim()); cur = ''; }
      cur += p;
    });
    if (cur.trim()) out.push(cur.trim());
    return out;
  }
  function readQueue() {
    var q = [];
    var sel = picked || selectedText();
    picked = '';
    if (sel) {
      /* the selection is already highlighted by the browser, so no ring */
      sel.split(/\n+/).forEach(function (line) {
        chunks(line.replace(/\s+/g, ' ').trim()).forEach(function (c) { if (c) q.push({ el: null, text: c }); });
      });
      return q;
    }
    var top = firstVisible('main,[role="main"]') || document.body;
    readBlocks(top).forEach(function (b) {
      chunks(b.text).forEach(function (c) { q.push({ el: b.el, text: c }); });
    });
    return q;
  }

  function readPlay() {
    if (!SPEECH) return;
    if (reader.status === 'paused') {
      window.speechSynthesis.resume();
      reader.status = 'playing';
      syncReader();
      return;
    }
    if (reader.status === 'playing') return;
    var q = readQueue();
    if (!q.length) { announce('Nothing to read on this page.'); return; }
    window.speechSynthesis.cancel();
    reader.queue = q;
    reader.i = 0;
    reader.status = 'playing';
    speakNext(++reader.run);
    syncReader();
  }
  /* `run` ties each utterance to one Play. Stop bumps it, so the end and
     error events of a cancelled utterance are ignored. */
  function speakNext(run) {
    if (run !== reader.run) return;
    var item = reader.queue[reader.i];
    if (!item) { readStop(); return; }
    var u = new window.SpeechSynthesisUtterance(item.text);
    u.rate = RATES[state.readRate];
    if (root.getAttribute('lang')) u.lang = root.getAttribute('lang');
    u.onend = function () { if (run === reader.run) { reader.i++; speakNext(run); } };
    u.onerror = function () { if (run === reader.run) readStop(); };
    reader.utt = u; /* kept, so the engine cannot drop it mid-sentence */
    mark(item.el);
    window.speechSynthesis.speak(u);
  }
  function readPause() {
    if (reader.status !== 'playing') return;
    window.speechSynthesis.pause();
    reader.status = 'paused';
    syncReader();
  }
  function readStop() {
    reader.run++;
    var was = reader.status;
    reader.status = 'idle';
    reader.queue = [];
    reader.utt = null;
    if (SPEECH && was !== 'idle') window.speechSynthesis.cancel();
    mark(null);
    syncReader();
  }

  /* The ring is our own element laid over the block, never a class or style
     on the block itself, so the page's markup is left exactly as it was. */
  function mark(el) {
    if (!markEl) return;
    reader.el = el;
    if (!el) {
      markEl.classList.remove('on');
      markEl.removeAttribute('style');
      return;
    }
    var r = el.getBoundingClientRect();
    if (el.scrollIntoView && (r.bottom < 0 || r.top > window.innerHeight)) el.scrollIntoView({ block: 'center' });
    markEl.classList.add('on');
    placeMark();
  }
  function placeMark() {
    if (!reader.el || !markEl) return;
    var r = reader.el.getBoundingClientRect(), s = markEl.style;
    s.top = r.top + 'px';
    s.left = r.left + 'px';
    s.width = r.width + 'px';
    s.height = r.height + 'px';
  }
  /* aria-disabled rather than disabled, so focus is not dropped when the
     button a keyboard user just pressed becomes unavailable. */
  function syncReader() {
    if (!shadow) return;
    var st = reader.status;
    shadow.querySelectorAll('[data-read]').forEach(function (b) {
      if (b.disabled) return;
      var a = b.getAttribute('data-read');
      var off = a === 'play' ? st === 'playing' : a === 'pause' ? st !== 'playing' : st === 'idle';
      b.setAttribute('aria-disabled', off ? 'true' : 'false');
    });
  }

  /* ---------- report a problem: copy the address ----------
     Email links do nothing for visitors who use email in the browser
     (Gmail, Outlook on the web), so the panel shows the address and copies
     it (D-31). Only the address goes on the clipboard: nothing about the
     visitor or their settings, and nothing is sent from here. The default
     address is two strings joined when the panel is built, so this file
     never holds it as one piece for an address harvester to find. */
  var MAIL_TO = ['hello', 'ryzoro.com'];
  var copiedTimer = 0;
  function reportAddress() {
    return CFG.reportEmail || MAIL_TO.join('@');
  }
  function copyAddress() {
    var clip = window.navigator && window.navigator.clipboard;
    var settled = false;
    var copied = function () {
      if (settled) return;
      settled = true;
      copyLabel('Copied');
      window.clearTimeout(copiedTimer);
      copiedTimer = window.setTimeout(function () { copyLabel('Copy'); }, 2000);
      announce('Address copied');
    };
    /* No Clipboard API (a page not served over https, an old browser), a
       refused permission, or a throw: the address is selected instead,
       ready for Ctrl+C. */
    var failed = function () {
      if (settled) return;
      settled = true;
      selectAddress();
      announce('Select and copy the address');
    };
    if (!clip || typeof clip.writeText !== 'function') return failed();
    try {
      var p = clip.writeText(reportAddress());
      if (p && typeof p.then === 'function') p.then(copied, failed);
      else copied();
    } catch (e) { failed(); }
  }
  function copyLabel(word) {
    copyBtn.querySelector('span').textContent = word;
  }
  function selectAddress() {
    var sel = window.getSelection ? window.getSelection() : null;
    if (!sel) return;
    try {
      var r = document.createRange();
      r.selectNodeContents(addrEl);
      sel.removeAllRanges();
      sel.addRange(r);
    } catch (e) { /* the visitor can still select it by hand */ }
  }

  /* ---------- mute sound ----------
     Sets the `muted` property on every <audio> and <video>, never the
     attribute, and remembers what each one was so turning it off puts every
     element back exactly as it was. New media are caught by the observer in
     watch(). A visitor who unmutes one video by hand is left alone. */
  var muted = window.Map ? new Map() : null;
  function muteOne(el) {
    if (!muted || muted.has(el)) return;
    muted.set(el, el.muted);
    el.muted = true;
  }
  function muteWithin(node) {
    if (node.nodeType !== 1) return;
    if (/^(AUDIO|VIDEO)$/.test(node.tagName)) muteOne(node);
    if (node.querySelectorAll) Array.prototype.forEach.call(node.querySelectorAll('audio,video'), muteOne);
  }
  function applyMute() {
    if (!muted) return;
    if (state.mute) { muteWithin(document.body); return; }
    muted.forEach(function (was, el) { el.muted = was; });
    muted.clear();
  }

  /* ---------- page wrapper for filter effects ----------
     Color strength and Invert colors genuinely need a CSS filter, and a filter
     creates a containing block for fixed-position descendants. So it goes on a
     wrapper around the page content that the toolbar sits OUTSIDE of. Created
     lazily, only if the visitor actually uses one of them, because reparenting
     body's children can disturb `body > *` selectors and scripts holding node
     references. Set filterEffects:false to drop both and never build it. */
  function pageWrap() {
    if (pageEl) return pageEl;
    if (!CFG.filterEffects || !document.body) return null;
    var mine = [host, guideEl, maskEl, tipEl, markEl, effectsEl];
    var kids = [], n;
    for (n = document.body.firstChild; n; n = n.nextSibling) kids.push(n);
    var w = document.createElement('div');
    w.id = PAGE_ID;
    document.body.insertBefore(w, document.body.firstChild);
    kids.forEach(function (k) {
      if (mine.indexOf(k) !== -1) return;
      /* moveBefore keeps an iframe loaded, a video playing and focus where it
         was; appendChild reloads every iframe it moves. Not every browser
         has moveBefore yet. */
      if (w.moveBefore) {
        try { w.moveBefore(k, null); return; } catch (e) { /* appendChild below */ }
      }
      w.appendChild(k);
    });
    pageEl = w;
    return w;
  }

  /* ---------- text scaling ---------- */
  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, HEAD: 1, META: 1, LINK: 1, BR: 1, HR: 1, IMG: 1, svg: 1, path: 1, g: 1, use: 1 };
  var FORM_TAGS = { INPUT: 1, TEXTAREA: 1, SELECT: 1, OPTION: 1, BUTTON: 1 };

  function basePx(el) {
    var raw = window.getComputedStyle(el).fontSize;
    if (!raw || raw.slice(-2) !== 'px') return null;
    var n = parseFloat(raw);
    return !n || isNaN(n) ? null : n;
  }
  function hasOwnText(el) {
    for (var c = el.firstChild; c; c = c.nextSibling) {
      if (c.nodeType === 3 && c.nodeValue.trim() !== '') return true;
    }
    return false;
  }
  /* What each element's inline style was before scaling touched it. Removing
     our font-size alone is not enough: the browser re-serialises the style
     attribute ("color:#8fd3e8" comes back as "color: rgb(143, 211, 232);"),
     and a font-size the site set inline would be lost. So the site's own
     font-size is put back, and if nothing else changed meanwhile the
     attribute is restored character for character. */
  var origStyle = window.Map ? new Map() : null;
  function remember(el) {
    if (!origStyle || origStyle.has(el)) return;
    origStyle.set(el, {
      attr: el.getAttribute('style'),
      size: el.style.getPropertyValue('font-size'),
      prio: el.style.getPropertyPriority('font-size'),
      css: el.style.cssText
    });
  }
  function unscaleAll() {
    var touched = document.querySelectorAll('[' + FS_ATTR + ']');
    for (var i = 0; i < touched.length; i++) {
      var el = touched[i], o = origStyle && origStyle.get(el);
      /* set in place rather than remove and re-add, which would move it to
         the end of the declaration list */
      if (o && o.size) el.style.setProperty('font-size', o.size, o.prio);
      else el.style.removeProperty('font-size');
      if (o && el.style.cssText === o.css) {
        /* Chrome writes a CSSOM change back to the attribute lazily. Removed
           while that write is pending, the attribute comes back as style="".
           Reading it first makes the write happen now. */
        if (o.attr === null) { el.getAttribute('style'); el.removeAttribute('style'); }
        else {
          el.setAttribute('style', o.attr);
          /* A CSP without 'unsafe-inline' blocks a style attribute set from
             script, which would strip the site's own inline styles. The
             CSSOM is not blocked, so fall back to it. */
          if (el.style.cssText !== o.css) el.style.cssText = o.css;
        }
      }
      el.removeAttribute(FS_ATTR);
    }
    if (origStyle) origStyle.clear();
  }
  /* Every size is read from the page as the site left it, and only then is
     any written. Reading and writing one element at a time, as this did
     until phase 4, read a child's size after its parent had grown, so text
     that inherits its size was scaled twice: 150% came out near 172%.
     FS_ATTR marks what was scaled and keeps the size it started from. */
  var scaled = 1;
  function scaleText(factor) {
    unscaleAll();
    scaled = factor;
    if (factor === 1) return;
    var todo = [];
    var bodyBase = basePx(document.body);
    if (bodyBase) todo.push([document.body, bodyBase]);
    var nodes = document.body.getElementsByTagName('*');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el === host || el === guideEl || el === tipEl || (host && host.contains(el))) continue;
      if (SKIP_TAGS[el.tagName] || SKIP_TAGS[el.nodeName]) continue;
      if (!hasOwnText(el) && !FORM_TAGS[el.tagName]) continue;
      var base = basePx(el);
      if (base !== null) todo.push([el, base]);
    }
    todo.forEach(function (t) {
      remember(t[0]);
      t[0].setAttribute(FS_ATTR, t[1]);
      t[0].style.setProperty('font-size', (t[1] * factor).toFixed(2) + 'px', 'important');
    });
  }

  /* ---------- apply state ---------- */
  function apply() {
    var cl = root.classList;

    /* invert/mono/cursor are still cleared: a browser holding an older cached
       copy of this script may have left those classes on the root. */
    ['dark', 'light', 'hc', 'invert', 'mono', 'cursor'].forEach(function (c) { cl.remove('a11yfx-' + c); });
    var scheme = state.contrast;
    if (scheme === 'auto') scheme = (darkQuery && darkQuery.matches) ? 'dark' : 'light';
    if (state.highContrast) {
      if (scheme !== 'dark') scheme = 'light';
      cl.add('a11yfx-hc');
    }
    if (scheme === 'dark' || scheme === 'light') cl.add('a11yfx-' + scheme);

    cl.toggle('a11yfx-font', state.readableFont);
    cl.toggle('a11yfx-dys', state.dyslexiaFont);
    cl.toggle('a11yfx-links', state.links);
    cl.toggle('a11yfx-heads', state.headings);
    cl.toggle('a11yfx-focus', state.focusRing);
    cl.toggle('a11yfx-cur-black', state.cursor === 'black');
    cl.toggle('a11yfx-cur-white', state.cursor === 'white');
    cl.toggle('a11yfx-motion', state.stopMotion);
    cl.toggle('a11yfx-guide', state.guide);
    cl.toggle('a11yfx-mask', state.mask);
    cl.toggle('a11yfx-noimg', state.hideImages);

    cl.remove('a11yfx-space1', 'a11yfx-space2');
    if (state.spacing > 0) cl.add('a11yfx-space' + state.spacing);
    cl.remove('a11yfx-word1', 'a11yfx-word2');
    if (state.wordSpacing > 0) cl.add('a11yfx-word' + state.wordSpacing);
    cl.remove('a11yfx-lh1', 'a11yfx-lh2', 'a11yfx-lh3');
    if (state.lineHeight > 0) cl.add('a11yfx-lh' + state.lineHeight);
    cl.remove('a11yfx-al1', 'a11yfx-al2', 'a11yfx-al3');
    if (state.align > 0) cl.add('a11yfx-al' + state.align);

    var pw = (state.saturation > 0 || state.invert) ? pageWrap() : pageEl;
    if (pw) {
      pw.classList.remove('a11yfx-sat1', 'a11yfx-sat2', 'a11yfx-sat3');
      if (state.saturation > 0) pw.classList.add('a11yfx-sat' + state.saturation);
      pw.classList.toggle('a11yfx-inv', !!state.invert);
    }

    applyColors();
    applyMute();
    if (!state.tooltips) hideTip();

    if (wrapEl) {
      var side = /left/.test(CFG.position) ? 'left' : 'right';
      if (state.dock === 'left' || state.dock === 'right') side = state.dock;
      wrapEl.classList.toggle('side-left', side === 'left');
      wrapEl.classList.toggle('side-right', side === 'right');
      wrapEl.classList.toggle('v-top', /^top/.test(CFG.position));
      wrapEl.classList.toggle('docked-hidden', state.dock === 'hidden');
      wrapEl.classList.toggle('hidden-now', hiddenNow);
      wrapEl.classList.toggle('big', !!state.oversized);
      wrapEl.classList.toggle('still', !!state.stopMotion);
      /* The panel follows the visitor's text size, and Bigger panel on top. */
      var px = 16 * SCALES[state.textScale] * (state.oversized ? 1.2 : 1);
      wrapEl.style.fontSize = Math.round(px * 100) / 100 + 'px';
    }

    if (!rootHadClass && root.getAttribute('class') === '') root.removeAttribute('class');
    /* Only when the size changed: no other setting moves it, and a rescale
       reads every text element on the page. Later content and breakpoint
       changes rescale from watch(). */
    if (SCALES[state.textScale] !== scaled) scaleText(SCALES[state.textScale]);
    watchDom();
    fitAids();
  }

  /* Rewrites the one effects stylesheet only when a custom colour or the
     dyslexia font changed. */
  function applyColors() {
    root.classList.toggle('a11yfx-fg', !!state.textColor);
    root.classList.toggle('a11yfx-bg', !!state.bgColor);
    if (!effectsEl) return;
    var extra = [colorCSS(), fontCSS()].filter(Boolean).join('\n');
    var css = extra ? EFFECTS + '\n' + extra : EFFECTS;
    if (effectsEl.textContent !== css) effectsEl.textContent = css;
  }

  function isOn(b) {
    var key = b.getAttribute('data-key'), raw = b.getAttribute('data-value');
    if (raw === null) return !!state[key];
    return String(state[key]) === raw;
  }

  function sync() {
    shadow.querySelectorAll('[data-out]').forEach(function (o) {
      var g = o.getAttribute('data-out'), txt = OUT[g][state[g]];
      /* only on change: the readout is a live region */
      if (o.textContent !== txt) o.textContent = txt;
      o.setAttribute('aria-valuenow', state[g]);
      o.setAttribute('aria-valuetext', txt);
    });
    /* aria-disabled, not disabled: a disabled button drops focus to <body>
       the moment a keyboard user reaches the end of the range. */
    shadow.querySelectorAll('.step').forEach(function (s) {
      var group = s.getAttribute('data-step');
      s.querySelector('[data-dir="-1"]').setAttribute('aria-disabled', state[group] === 0 ? 'true' : 'false');
      s.querySelector('[data-dir="1"]').setAttribute('aria-disabled', state[group] === MAXES[group] ? 'true' : 'false');
    });
    shadow.querySelectorAll('.sw').forEach(function (b) {
      b.setAttribute('aria-checked', isOn(b) ? 'true' : 'false');
    });
    shadow.querySelectorAll('.seg button').forEach(function (b) {
      b.setAttribute('aria-pressed', isOn(b) ? 'true' : 'false');
    });
    shadow.querySelectorAll('.clr input').forEach(function (inp) {
      var key = inp.getAttribute('data-key');
      inp.value = state[key] || SWATCH[key];
    });
    shadow.querySelectorAll('.rst').forEach(function (b) {
      b.setAttribute('aria-disabled', state[b.getAttribute('data-key')] ? 'false' : 'true');
    });
    syncReader();
    renderLists();
  }

  /* Reset also stops reading aloud, which takes the ring off the page. */
  function resetAll() {
    if (reader.status !== 'idle') readStop();
    state = Object.assign({}, DEFAULTS);
    hiddenNow = false;
    saveHidden();
    apply(); save(); sync();
    announce('All settings reset');
  }

  /* ---------- react to later DOM and viewport changes ----------
     The observer is connected only while something needs to follow the
     page: Mute sound, Bigger text, or a list open in the panel. Turning the
     last of those off, or Reset all, disconnects it, so a toolbar nobody
     has used watches nothing. */
  var observer = null, domTimer = null;
  function onMutations(records) {
    /* Our own nodes changing (a tooltip's text, say) is not the page changing. */
    records = records.filter(function (r) { return !mine(r.target); });
    if (!records.length) return;
    /* Muting is not debounced: a video that autoplays would be heard. */
    if (state.mute) {
      records.forEach(function (rec) { Array.prototype.forEach.call(rec.addedNodes, muteWithin); });
    }
    if (state.textScale === 0 && !(panelOpen && (state.structure || state.linkList))) return;
    window.clearTimeout(domTimer);
    domTimer = window.setTimeout(function () {
      if (state.textScale > 0) scaleText(SCALES[state.textScale]);
      if (panelOpen) renderLists();
    }, 250);
  }
  function watchDom() {
    var need = state.mute || state.textScale > 0 || (panelOpen && (state.structure || state.linkList));
    if (need && !observer && window.MutationObserver) {
      observer = new MutationObserver(onMutations);
      observer.observe(document.body, { childList: true, subtree: true });
    } else if (!need && observer) {
      observer.disconnect();
      observer = null;
      window.clearTimeout(domTimer);
    }
  }
  function watch() {
    /* A breakpoint can change the site's own sizes, and the inline
       !important we write would otherwise pin the old ones forever. */
    var rt = null;
    window.addEventListener('resize', function () {
      if (state.textScale === 0) return;
      window.clearTimeout(rt);
      rt = window.setTimeout(function () { scaleText(SCALES[state.textScale]); }, 250);
    });
  }

  /* ---------- init ---------- */
  function init() {
    if (!build()) return;
    apply();
    /* Write the sanitised state straight back, so a value this build rejected
       (a removed mode, an out-of-range step, hand-edited storage) is cleaned out
       of localStorage permanently rather than being re-sanitised on every load. */
    save();
    sync();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
